// Updated Compass App.tsx - Cleaned Up for Production
import React, { useState, useEffect, useRef } from 'react';
import { authHeaders } from '../../services/authHeaders';
import { getStoredSessionId, resolveCurrentSessionId } from '../../services/currentSession';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useLocation } from 'react-router-dom';
import apiConfig from '../../config/apiConfig';
import { motion } from 'framer-motion';
import {
  AlertTriangle, Shield, TrendingUp,
  Building2, User, Target,
  CheckCircle, AlertCircle, XCircle, Users, Clock,
  TrendingDown, BarChart3, Zap, Globe, Lock, Home,
  FileText, Settings, LogOut, RefreshCw, Plus, Download,
  History, ArrowLeft, Loader2, HelpCircle, Info,
  ChevronDown, ChevronUp, Activity
} from 'lucide-react';
import HelpTooltip from '../../shared/components/HelpTooltip';
import HowItWorksGuide from '../../shared/components/HowItWorksGuide';
import { NotMeasured } from '../../shared/components/NotMeasured';
import DataSourceIndicator from './components/DataSourceIndicator';
// import UnifiedNavigation from './components/UnifiedNavigation'; // Removed - handled by AppLayout
import EnhancedThreatLandscape from './components/dashboard/EnhancedThreatLandscape';

import OutsidePressure from './pages/OutsidePressure';
import OutsidePressuresCenter from '../defense/components/OutsidePressuresCenter';
import SettingsPage from './pages/Settings';
import BusinessIntelligentCards from './components/BusinessIntelligentCards';
import TriangulationDrawer from './components/TriangulationDrawer';
import { BrokerPlacementCard } from './components/broker/BrokerPlacementCard';
import type { Placement } from './components/broker/types';

import CyberPovertyLineChart from './components/charts/CyberPovertyLineChart';
import CyberRiskSignal from './components/dashboard/CyberRiskSignal';
import { boardReportRiskFields } from './boardReportRiskFields';
import { complianceFactsDisplay, cmmcHeadingLabel, type ComplianceFacts } from '../../shared/components/prismFacts/complianceFacts';
import { getReadinessBandLabel, getReadinessBandStyle, getRiskBandLabel, getRiskBandStyle, getRelativeComparison } from '../../utils/bands';

// ============ TYPE DEFINITIONS ============
interface UserType {
  id: string;
  name: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

interface IndustryBenchmark {
  averageReadiness: number;
  averageRiskScore: number;
  averageRecoveryDays: number;
  revenueAtRiskPercent: number | string | null;
  recommendedSecurityPercent: number;
  investmentRange: {
    min: number;
    optimal: number;
    max: number;
  };
}

interface InvestmentScenario {
  label: string;
  investmentPercent: number;
  investment: number;
  readiness: number;
  riskMultiplier?: number;
  risk: number;
  roi?: boolean;
  investmentFormatted: string;
  riskFormatted: string;
  description?: string;
  isCurrent?: boolean;
}

interface Big6UnifiedParams {
  big6Scores: Record<string, number>;
  gapAnalysis: {
    weightedAverageGap: number;
    topRiskCategories: string[];
    categoryGaps: Record<string, { gap: number; weight: number; label: string }>;
  };
  investmentTargets: {
    optimalReductionTarget: number;
    maxReductionTarget: number;
  };
  povertyLine: {
    povertyLinePercent: number;
    optimalPercent: number;
    excessivePercent: number;
    basePercent: number;
    industryAdjustment: number;
    gapAdjustment: number;
  };
  budgetEvaluation: {
    budgetStatus: string;
    budgetGap: number;
    currentPercent: number;
    povertyLinePercent: number;
  };
  monteCarloVolatility: Record<string, { gap: number; volatility: number; label: string }>;
}

interface BusinessIntelligenceData {
  assessmentId: number;
  companyName: string;
  industry: string;
  annualRevenue: number | null;
  revenueAtRisk: string | null;
  revenueAtRiskRaw: number | null;
  recoveryDays: number;
  riskScore: number;
  readinessScore: number;
  isActualRevenue: boolean;
  calculationSource: string;
  recoveryLevel?: string;
  dailyRevenue?: number;
  industryBenchmark?: IndustryBenchmark;
  investmentScenarios?: InvestmentScenario[];
  boardActionItems?: {
    immediate: string[];
    strategic: string[];
    governance: string[];
  };
  advancedAnalytics?: {
    big6UnifiedParams?: Big6UnifiedParams;
    monteCarloResults?: any;
  };
  // Broker placement (cyber-insurance) — present ONLY when BROKER_PLACEMENT_ENABLED
  // is set on the backend; undefined otherwise (gates the card render).
  placement?: Placement | null;
  prismContext?: {
    // Not for display (no compliance score outside SPRS, 2026-10-29); kept in the payload.
    compliance_coverage_pct: number | null;
    // PRISM #291 facts pooled across projects; absent on an older backend → "Not measured".
    compliance_facts?: ComplianceFacts | null;
    pdr_maturity: { protect: number; detect: number; respond: number } | null;
    // CSF 2.0 Phase 1 — PRISM Recovery Coverage on 0-1 scale. Sibling axis to
    // pdr_maturity (different methodology — parallel-probability vs raw AVG).
    recovery_coverage_pct?: number | null;
    top_gaps: Array<{ control: string; framework: string; severity: string; description: string }>;
    evidence_count: number;
    deployed_tools: Array<{ name: string; type?: string; criticality?: string }>;
    deployed_controls: Array<{ name: string; type?: string; effectiveness?: number; status?: string; nist_controls?: string[] }>;
    frameworks_assessed: string[];
    project_count: number;
    total_assets: number;
    total_countermeasures: number;
  } | null;
  // CSF 2.0 Phase 1 Slice 3 — Foundations × PRISM Recovery Cross-Check
  // (FOUNDATIONS_RECOVERY_CROSSCHECK_SPEC.md v0.1). present=false when no
  // PRISM signal or recoveryDays missing; divergent=true gates the Board
  // Report coaching narrative. direction labels feed the v1 static template
  // until Iris is wired in to draft customized prose.
  recoveryCrossCheck?: {
    present: boolean;
    recoveryDays: number | null;
    impliedDays: number | null;
    deltaRatio: number | null;
    divergent: boolean;
    direction: 'aligned' | 'overcautious' | 'overcredited' | null;
    thresholdPct: number;
  };
  // FOUNDATIONS_AI_ROADMAP Phase 5 Slice 1 — Foundations × PRISM Compliance
  // Narrative (FOUNDATIONS_PRISM_COMPLIANCE_NARRATIVE_SPEC.md v0.1). present=false
  // when no PRISM compliance signal (no link, no compliance_coverage_pct on the
  // snapshot). present=true renders a single factual sentence on the Board Report
  // showing PRISM-side coverage of the customer's primary framework.
  // 2026-10-29: the sentence states facts (standards, open gaps, % evaluated), never a
  // compliance %; present=false when PRISM's facts are not measured.
  prismComplianceNarrative?: {
    present: boolean;
    sentence: string | null;
    primaryFramework: string | null;
    frameworkCount: number;
    openGaps?: number | null;
    criticalOpenGaps?: number | null;
    evaluatedPct?: number | null;
  };
  // FOUNDATIONS_AI_ROADMAP Phase 5 Slice 5 — PRISM P/D/R maturity acknowledgment.
  // Pure deterministic; surfaces protect/detect/respond on 0-4 DoDCAR scale
  // with weakest-function call-out. CSF 2.0 Recover excluded — recovery has
  // its own panel on a different scale.
  prismMaturityNarrative?: {
    present: boolean;
    sentence: string | null;
    protect: number | null;
    detect: number | null;
    respond: number | null;
    balanced: boolean;
    weakest: 'Protect' | 'Detect' | 'Respond' | null;
    threshold: number;
    scoredCount: number;
  };
  // FOUNDATIONS_AI_ROADMAP Phase 5 Slice 6 — PRISM evidence-density acknowledgment.
  // Pure deterministic; surfaces evidence_count + top-5 deployed tools by
  // criticality with engagement-maturity band (early-stage / active / established).
  prismEvidenceNarrative?: {
    present: boolean;
    sentence: string | null;
    evidenceCount: number | null;
    toolCount: number;
    topTools: string[];
    maturity: 'early-stage' | 'active' | 'established' | null;
  };
  // FOUNDATIONS_AI_ROADMAP Phase 5 Slice 4 — PRISM CMMC Level 2 readiness milestone.
  // Pure deterministic; surfaces controls_met / controls_partial / total_controls
  // with banded framing, never a score (not_started / early-stage / underway /
  // approaching-readiness / ready) and a stale-data tail when synced_at > 7 days.
  prismCmmcL2TimelineNarrative?: {
    present: boolean;
    sentence: string | null;
    controlsMet: number | null;
    controlsPartial: number | null;
    controlsNotMet?: number | null;
    controlsNotAssessed?: number | null;
    // APPLICABLE controls (PRISM #291), not the number assessed.
    totalControls: number | null;
    pctMet: number | null;
    band: 'not_started' | 'early-stage' | 'underway' | 'approaching-readiness' | 'ready' | null;
    syncedDaysAgo: number | null;
    // "CMMC Level 1|2|3" from PRISM's target_level, else "CMMC". Absent on an older backend.
    cmmcLabel?: string | null;
  };
}

interface NavigationHeaderProps {
  title: string;
  subtitle?: string;
  user?: UserType;
  onLogout?: () => void;
  onRefresh?: () => void;
  onThreatAnalysis?: () => void;
  onDefensePlanning?: () => void;
  onAssessmentDashboard?: () => void;
}

interface ExecutiveSummaryBannerProps {
  businessData: BusinessIntelligenceData | null;
  loading: boolean;
  onRefresh: () => void;
}

interface BusinessIntelligenceDashboardProps {
  businessData: BusinessIntelligenceData | null;
  loading: boolean;
}

interface NavigationItem {
  name: string;
  id: string;
  icon: React.ComponentType<{ className?: string }>;
}

// Legacy Tooltip removed — replaced by shared HelpTooltip component

// ============ NAVIGATION HEADER COMPONENT (CLEANED UP) ============
// COMMENTED OUT - No longer needed with UnifiedNavigation
/*
const NavigationHeader: React.FC<NavigationHeaderProps> = ({
  title,
  subtitle,
  onLogout,
  onRefresh,
  onThreatAnalysis,
  onDefensePlanning,
  onAssessmentDashboard
}) => {
  return (
    <header className="ra-glass shadow-[0_1px_3px_rgba(0,0,0,0.3)] border-b border-[rgba(255,255,255,0.12)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 border-b border-[rgba(255,255,255,0.12)]">
          <div className="flex items-center space-x-4">
            <BarChart3 aria-hidden="true" className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-[#e6f1ff]">{title}</h1>
              {subtitle && <p className="text-sm text-[#8892b0]">{subtitle}</p>}
            </div>
          </div>
        </div>
        
        <div className="py-3">
          <div className="flex items-center justify-start space-x-3 flex-wrap">
            {onLogout && (
              <button
                onClick={onLogout}
                className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <LogOut aria-hidden="true" className="w-4 h-4 mr-2" />
                Logout
              </button>
            )}
            
            {onRefresh && (
              <button
                onClick={onRefresh}
                className="flex items-center justify-center px-4 py-2 bg-[rgba(255,255,255,0.20)] text-white rounded-lg hover:bg-[#1e3148] transition-colors"
              >
                <RefreshCw aria-hidden="true" className="w-4 h-4 mr-2" />
                Refresh Data
              </button>
            )}
            
            <div className="h-6 border-l border-[rgba(255,255,255,0.18)]"></div>
            
            {onThreatAnalysis && (
              <button
                onClick={onThreatAnalysis}
                className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Shield aria-hidden="true" className="w-4 h-4 mr-2" />
                Threat Analysis
              </button>
            )}

            {onDefensePlanning && (
              <button
                onClick={onDefensePlanning}
                className="flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Target aria-hidden="true" className="w-4 h-4 mr-2" />
                Risk Optimization
              </button>
            )}

            {onAssessmentDashboard && (
              <button
                onClick={onAssessmentDashboard}
                className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <BarChart3 aria-hidden="true" className="w-4 h-4 mr-2" />
                Risk Dashboard
              </button>
            )}
            
            <div className="h-6 border-l border-[rgba(255,255,255,0.18)]"></div>
            
            <button
              onClick={() => { }}
              className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download aria-hidden="true" className="w-4 h-4 mr-2" />
              Export Intelligence
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};
*/

// Executive Summary Banner Component (CLEANED UP)
const ExecutiveSummaryBanner: React.FC<ExecutiveSummaryBannerProps> = ({ 
  businessData, 
  loading, 
  onRefresh 
}) => {
  if (loading) {
    return (
      <div className="mb-8 p-6 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg text-white">
        <div className="flex items-center justify-center">
          <Loader2 aria-hidden="true" className="w-8 h-8 animate-spin mr-4" />
          <span>Loading business intelligence...</span>
        </div>
      </div>
    );
  }

  if (!businessData) {
    return (
      <div className="mb-8 p-6 bg-gradient-to-r from-red-600 to-pink-600 rounded-lg text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Unable to Load Business Data</h2>
            <p className="text-red-100">Please check your connection and try again</p>
          </div>
          <button
            onClick={onRefresh}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
          >
            <RefreshCw aria-hidden="true" className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8 p-6 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg text-white">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-bold">{businessData.companyName} - Executive Risk Summary</h2>
          {/* CLEANED UP: Removed database API reference */}
          <p className="text-green-100">
            Real-time risk intelligence
          </p>
          <div className="mt-2 text-sm">
            <span className="bg-white/20 px-2 py-1 rounded">
              Based on Assessment Data • 
              Actual Revenue: {businessData.isActualRevenue ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold">
            {(businessData.revenueAtRisk === null || businessData.revenueAtRisk === undefined)
              ? 'Not measured'
              : businessData.revenueAtRisk}
          </div>
          <div className="text-sm text-green-100">Revenue at Risk</div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold">
                ${(businessData.annualRevenue / 1000000).toFixed(1)}M
              </div>
              <div className="text-sm text-green-100">Annual Revenue</div>
            </div>
            <TrendingUp aria-hidden="true" className="h-8 w-8 text-green-300" />
          </div>
        </div>
        
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold">{getReadinessBandLabel(businessData.readinessScore || (100 - businessData.riskScore))}</div>
              <div className="text-sm text-green-100">Security Readiness</div>
            </div>
            <Shield aria-hidden="true" className="h-8 w-8 text-blue-300" />
          </div>
        </div>
        
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold">{businessData.recoveryDays}</div>
              <div className="text-sm text-green-100">Recovery Days</div>
            </div>
            <Clock aria-hidden="true" className="h-8 w-8 text-orange-300" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Business Intelligence Dashboard Component (CLEANED UP)
const BusinessIntelligenceDashboard: React.FC<BusinessIntelligenceDashboardProps> = ({
  businessData,
  loading
}) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>('30d');
  const [outsidePressureData, setOutsidePressureData] = useState<any>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string>('');
  const [sectionsExpanded, setSectionsExpanded] = useState({
    benchmarks: false,
    insights: false,
    threats: false
  });

  const toggleSection = (section: 'benchmarks' | 'insights' | 'threats') => {
    setSectionsExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Colorblind-friendly color classes
  const colors = {
    primary: 'bg-[#1e3a5f]',      // Deep Blue
    critical: 'bg-[#d35400]',      // Burnt Orange
    warning: 'bg-[#f39c12]',       // Amber
    stable: 'bg-[#0d9488]',        // Teal
    accent: 'bg-[#7c3aed]',        // Purple
    neutral: 'bg-[#475569]'        // Slate
  };

  // Session to show: URL, stored, then the caller's own latest (services/currentSession.ts).
  // null means the viewer has no assessment — never substitute an id.
  const getAssessmentId = (): Promise<string | null> => resolveCurrentSessionId();
  
  // Fetch outside pressure data and set session ID
  React.useEffect(() => {
    const fetchOutsidePressureData = async () => {
      try {
        const assessmentId = await getAssessmentId();
        if (!assessmentId) return;
        setCurrentSessionId(assessmentId); // Store for CRS component
        const response = await fetch(apiConfig.riskIntelligence.getOutsidePressures(assessmentId), { headers: authHeaders() });
        if (response.ok) {
          const result = await response.json();
          console.log('Outside Pressure Data received:', result);
          if (result.success && result.outsidePressures) {
            // Include personalizationContext with the outsidePressures data
            const dataWithContext = {
              ...result.outsidePressures,
              personalizationContext: result.personalizationContext
            };
            setOutsidePressureData(dataWithContext);
            console.log('Outside Pressure Data set with context:', dataWithContext);
          }
        }
      } catch (error) {
        console.error('Failed to fetch outside pressure data:', error);
      }
    };

    if (businessData) {
      fetchOutsidePressureData();
    }
  }, [businessData]);
  
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 aria-hidden="true" className="w-8 h-8 animate-spin text-blue-600 mr-4" />
            <span className="text-[#a8b2d1]">Loading business intelligence...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!businessData) {
    return (
      <div className="space-y-6">
        <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
          <div className="text-center py-12">
            <AlertCircle aria-hidden="true" className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#e6f1ff] mb-2">
              Unable to Load Business Intelligence
            </h3>
            <p className="text-[#a8b2d1]">
              Connection failed. Please check your connection.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* How Business Intelligence Works — collapsible module guide */}
      <HowItWorksGuide
        moduleName="Business Intelligence"
        steps={[
          { title: 'Cyber Risk Signal', desc: 'Your CRS combines all persona responses, weighting factors, and outside-pressures signals into a single 0-100 score with industry benchmarking. Hover any element for details on how it was computed.' },
          { title: 'Recovery Timeline', desc: 'Days to restore full operations after a major incident — calibrated against your assessment plus connected PRISM technical data (if available). The respond-maturity panel shows how PRISM telemetry sharpens this estimate.' },
          { title: 'Outside Pressures', desc: 'Strategic signals affecting your sector — geopolitical risk, regulatory shifts, supply-chain exposure, market conditions. These are STRATEGIC indicators, not technical CVE/KEV feeds.' },
          { title: 'Cyber Poverty Line', desc: 'Where your cyber budget falls relative to the industry-specific minimum effective spend. Below the line = under-resourced. Above the line is necessary but not sufficient — pair with coverage data.' },
          { title: 'Business Intelligence Cards', desc: 'Revenue at risk, annual loss expectancy, breach cost models — all driven by your actual revenue, headcount, and industry-specific incident benchmarks. Click any card for the underlying assumptions.' },
        ]}
        footnote="Data refreshes whenever a persona submits new responses or when PRISM data is re-imported. The Export button generates a board-ready PDF."
      />

      {/* HERO SECTION: CRS + Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cyber Risk Signal - Hero Card (spans 2 columns) */}
        <div className="lg:col-span-2">
          {currentSessionId && (
            <CyberRiskSignal sessionId={currentSessionId} />
          )}
        </div>

        {/* Quick Stats Column */}
        <div className="space-y-4">
          {/* Recovery Timeline */}
          <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-5 border-l-4 border-[#0d9488]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#a8b2d1] mb-1">Recovery Timeline</p>
                <p className="text-2xl font-bold text-[#e6f1ff]">{businessData.recoveryDays} days</p>
                <p className="text-xs text-[#8892b0]">to full operations</p>
              </div>
              <Clock aria-hidden="true" className="h-8 w-8 text-[#0d9488]" />
            </div>
            {businessData.prismContext?.pdr_maturity?.respond != null && (() => {
              const respond = Number(businessData.prismContext.pdr_maturity.respond);
              if (isNaN(respond)) return null;
              return (
                <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.08)]">
                  <p className="text-xs text-[#8892b0] flex items-center gap-1.5">
                    <AlertTriangle aria-hidden="true" className={`h-3.5 w-3.5 flex-shrink-0 ${
                      respond >= 3 ? 'text-[#22c55e]' :
                      respond >= 2 ? 'text-[#f59e0b]' : 'text-[#ef4444]'
                    }`} />
                    <span>
                      Respond Maturity: {respond.toFixed(1)}/4.0
                      {' \u2014 '}
                      {respond >= 3 ? 'high confidence estimate' :
                       respond >= 2 ? 'recovery estimate carries moderate uncertainty' :
                       'recovery estimate carries significant uncertainty'}
                    </span>
                  </p>
                </div>
              );
            })()}

            {/* CSF 2.0 Phase 1 Slice 3 \u2014 Recovery Cross-Check (Foundations \u00d7 PRISM).
                Renders ONLY when divergent=true per FOUNDATIONS_RECOVERY_CROSSCHECK_SPEC.md
                v0.1 gate matrix. No PRISM link / no RC% / aligned-within-threshold all
                stay silent. Purple accent (#a855f7) matches the PRISM Recovery Coverage
                card so users learn the visual language across both products. */}
            {businessData.recoveryCrossCheck?.divergent === true && (
              <div
                className="mt-3 pt-3 border-t border-[rgba(168,85,247,0.25)]"
                role="region"
                aria-label="Recovery cross-check finding"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle
                    aria-hidden="true"
                    className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#a855f7]"
                  />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-[#a855f7] mb-1">
                      Strategic vs technical recovery posture differs
                    </p>
                    <p className="text-xs text-[#8892b0] leading-relaxed">
                      Strategic estimate: <span className="font-semibold text-[#e6f1ff]">{businessData.recoveryCrossCheck.recoveryDays} days</span>
                      {' \u2014 '}
                      PRISM technical posture implies <span className="font-semibold text-[#e6f1ff]">{businessData.recoveryCrossCheck.impliedDays} days</span>
                      {' '}(CSF 2.0 Recovery Coverage compounded across recover-relevant ATT&amp;CK techniques).
                    </p>
                    <p className="text-xs text-[#8892b0] leading-relaxed mt-1.5">
                      {businessData.recoveryCrossCheck.direction === 'overcautious'
                        ? 'Your self-reported recovery estimate is more cautious than your deployed backup/DR capabilities warrant. Likely cause: incident response plan under-tested. Recommend a tabletop exercise to reconcile assumptions with actual capability.'
                        : 'Your self-reported recovery estimate is more optimistic than your deployed backup/DR capabilities support. Likely cause: backups untested or DR runbook incomplete. Recommend verifying restore procedures end-to-end before the next assessment cycle.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* FOUNDATIONS_AI_ROADMAP Phase 5 Slice 1 \u2014 PRISM compliance narrative.
                Single factual sentence; renders ONLY when present=true (i.e., the
                PRISM-side snapshot carries a compliance_coverage_pct). Silent on
                Foundations-only sessions OR PRISM-linked sessions without compliance
                assessments. Purple accent matches recovery cross-check so users
                learn the visual language for cross-product signals. */}
            {businessData.prismComplianceNarrative?.present === true && (
              <div
                className="mt-3 pt-3 border-t border-[rgba(168,85,247,0.25)]"
                role="region"
                aria-label="PRISM compliance coverage"
              >
                <div className="flex items-start gap-2">
                  <Shield
                    aria-hidden="true"
                    className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#a855f7]"
                  />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-[#a855f7] mb-1">
                      PRISM compliance posture
                    </p>
                    <p className="text-xs text-[#8892b0] leading-relaxed">
                      {businessData.prismComplianceNarrative.sentence}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* FOUNDATIONS_AI_ROADMAP Phase 5 Slice 5 — PRISM P/D/R maturity narrative.
                Surfaces protect/detect/respond on 0-4 DoDCAR scale with weakest-function
                call-out. Renders ONLY when present=true (i.e., at least one P/D/R value
                is on the snapshot). Silent on Foundations-only sessions or PRISM sessions
                without scoring yet. Purple accent matches the other cross-product panels. */}
            {businessData.prismMaturityNarrative?.present === true && (
              <div
                className="mt-3 pt-3 border-t border-[rgba(168,85,247,0.25)]"
                role="region"
                aria-label="PRISM P/D/R maturity"
              >
                <div className="flex items-start gap-2">
                  <Shield
                    aria-hidden="true"
                    className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#a855f7]"
                  />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-[#a855f7] mb-1">
                      PRISM P/D/R maturity
                    </p>
                    <p className="text-xs text-[#8892b0] leading-relaxed">
                      {businessData.prismMaturityNarrative.sentence}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* FOUNDATIONS_AI_ROADMAP Phase 5 Slice 6 — PRISM evidence-density narrative.
                Surfaces evidence_count + top-5 deployed tools (by criticality) with
                engagement-maturity band. Renders ONLY when present=true (evidence_count
                is on the snapshot). 0 evidence is a valid value — renders cleanly with
                early-stage band; silent reserved for missing data. */}
            {businessData.prismEvidenceNarrative?.present === true && (
              <div
                className="mt-3 pt-3 border-t border-[rgba(168,85,247,0.25)]"
                role="region"
                aria-label="PRISM evidence density"
              >
                <div className="flex items-start gap-2">
                  <Shield
                    aria-hidden="true"
                    className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#a855f7]"
                  />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-[#a855f7] mb-1">
                      PRISM engagement density
                    </p>
                    <p className="text-xs text-[#8892b0] leading-relaxed">
                      {businessData.prismEvidenceNarrative.sentence}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* FOUNDATIONS_AI_ROADMAP Phase 5 Slice 4 — PRISM CMMC L2 readiness milestone.
                Surfaces controls_met / total_controls / score from framework_scores with
                banded framing. Renders ONLY when present=true (CMMC L2 in scope). Stale-data
                tail (>7 days) is baked into the sentence by the service. */}
            {businessData.prismCmmcL2TimelineNarrative?.present === true && (
              <div
                className="mt-3 pt-3 border-t border-[rgba(168,85,247,0.25)]"
                role="region"
                aria-label={`PRISM ${cmmcHeadingLabel(businessData.prismCmmcL2TimelineNarrative)} readiness`}
              >
                <div className="flex items-start gap-2">
                  <Shield
                    aria-hidden="true"
                    className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#a855f7]"
                  />
                  <div className="flex-1">
                    {/* The level comes from PRISM's target_level ("CMMC Level 1/2/3"); without it,
                        plain "CMMC". Never guessed from the counts. */}
                    <p className="text-xs font-semibold text-[#a855f7] mb-1">
                      PRISM {cmmcHeadingLabel(businessData.prismCmmcL2TimelineNarrative)} readiness
                    </p>
                    <p className="text-xs text-[#8892b0] leading-relaxed">
                      {businessData.prismCmmcL2TimelineNarrative.sentence}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Optimization Level */}
          <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-5 border-l-4 border-[#7c3aed]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#a8b2d1] mb-1 flex items-center gap-1.5">
                  Optimization Level <HelpTooltip contentKey="compass.optimizationLevel" side="right" />
                </p>
                <p className="text-2xl font-bold text-[#e6f1ff]">
                  {(businessData.readinessScore || (100 - businessData.riskScore)) >= 65 ? 'High' :
                   (businessData.readinessScore || (100 - businessData.riskScore)) >= 45 ? 'Moderate' : 'Low'}
                </p>
                <p className="text-xs text-[#8892b0]">
                  {getReadinessBandLabel(businessData.readinessScore || (100 - businessData.riskScore))} readiness
                </p>
              </div>
              <TrendingUp aria-hidden="true" className="h-8 w-8 text-[#7c3aed]" />
            </div>
          </div>

          {/* Risk Event Potentiality */}
          {outsidePressureData && (
            <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-5 border-l-4 border-[#d35400]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#a8b2d1] mb-1">Risk Event Potentiality</p>
                  <p className="text-2xl font-bold text-[#e6f1ff]">
                    {(() => {
                      const score = outsidePressureData.overallScore;
                      if (!score) return 'N/A';
                      return score >= 80 ? 'High' : score >= 60 ? 'Moderate' : 'Low';
                    })()}
                  </p>
                  <p className="text-xs text-[#8892b0]">
                    {getRiskBandLabel(outsidePressureData.overallScore || 0)} external pressure
                  </p>
                </div>
                <AlertTriangle aria-hidden="true" className="h-8 w-8 text-[#d35400]" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* COLLAPSIBLE: Industry Benchmarks */}
      <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] overflow-hidden">
        <button
          onClick={() => toggleSection('benchmarks')}
          className="w-full px-6 py-4 flex items-center justify-between bg-[rgba(255,255,255,0.03)] hover:bg-[#172a45] transition-colors"
        >
          <div className="flex items-center">
            <Users aria-hidden="true" className="h-5 w-5 text-[var(--ra-cyber-aqua)] mr-3" />
            <div className="text-left">
              <h3 className="text-lg font-semibold text-[#e6f1ff]">Industry Benchmarks</h3>
              <p className="text-sm text-[#8892b0]">How you compare to {businessData.industry} peers</p>
            </div>
          </div>
          {sectionsExpanded.benchmarks ? (
            <ChevronUp aria-hidden="true" className="h-5 w-5 text-[#5a6a8a]" />
          ) : (
            <ChevronDown aria-hidden="true" className="h-5 w-5 text-[#5a6a8a]" />
          )}
        </button>

        {sectionsExpanded.benchmarks && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="p-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Readiness vs Peers */}
              {(() => {
                const readiness = businessData.readinessScore || (100 - businessData.riskScore);
                const peerAvg = businessData.industryBenchmark?.averageReadiness || 58;
                const readStyle = getReadinessBandStyle(readiness);
                const relComp = getRelativeComparison(peerAvg, readiness); // inverted: for readiness, higher = better, so compare peer→us
                const isAbove = readiness > peerAvg;
                return (
                  <div className="p-4 rounded-lg bg-[rgba(255,255,255,0.03)] border-l-4 border-[#1e3a5f]">
                    <p className="text-sm text-[#a8b2d1] flex items-center gap-1.5">Security Readiness <HelpTooltip content="Derived from your assessment responses across all six risk dimensions. This is a self-reported score — it reflects organizational awareness, not validated technical controls. A high readiness score with red Big 6 bars may indicate optimistic self-assessment." side="right" /></p>
                    <p className="text-2xl font-bold" style={{ color: readStyle.text }}>{readStyle.icon} {readStyle.label}</p>
                    <p className="text-xs text-[#8892b0]">{isAbove ? 'Above' : 'Below'} {businessData.industry} industry average</p>
                  </div>
                );
              })()}

              {/* Revenue Impact vs Peers */}
              <div className="p-4 rounded-lg bg-[rgba(255,255,255,0.03)] border-l-4 border-[#d35400]">
                <p className="text-sm text-[#a8b2d1]">Revenue Exposure</p>
                <p className="text-2xl font-bold text-[#d35400]">
                  {(businessData.annualRevenue === null || businessData.annualRevenue === undefined ||
                    businessData.revenueAtRiskRaw === null || businessData.revenueAtRiskRaw === undefined)
                    ? 'Not measured'
                    : `${((businessData.revenueAtRiskRaw / businessData.annualRevenue) * 100).toFixed(1)}%`}
                </p>
                <p className="text-xs text-[#8892b0]">
                  Industry avg:{' '}
                  {(businessData.industryBenchmark?.revenueAtRiskPercent === null || businessData.industryBenchmark?.revenueAtRiskPercent === undefined)
                    ? 'Not measured'
                    : `${businessData.industryBenchmark.revenueAtRiskPercent}%`}
                </p>
              </div>

              {/* Investment Level vs Peers */}
              <div className="p-4 rounded-lg bg-[rgba(255,255,255,0.03)] border-l-4 border-[#0d9488]">
                <p className="text-sm text-[#a8b2d1]">Recommended Investment</p>
                <p className="text-2xl font-bold text-[#0d9488]">
                  {(businessData.annualRevenue === null || businessData.annualRevenue === undefined)
                    ? 'Not measured'
                    : `$${((businessData.annualRevenue * (businessData.industryBenchmark?.recommendedSecurityPercent || 3.5) / 100) / 1000000).toFixed(1)}M`}
                </p>
                <p className="text-xs text-[#8892b0]">{businessData.industryBenchmark?.recommendedSecurityPercent || 3.5}% of revenue</p>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* COLLAPSIBLE: Strategic Insights */}
      <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] overflow-hidden">
        <button
          onClick={() => toggleSection('insights')}
          className="w-full px-6 py-4 flex items-center justify-between bg-[rgba(255,255,255,0.03)] hover:bg-[#172a45] transition-colors"
        >
          <div className="flex items-center">
            <BarChart3 aria-hidden="true" className="h-5 w-5 text-[var(--ra-cyber-aqua)] mr-3" />
            <div className="text-left">
              <h3 className="text-lg font-semibold text-[#e6f1ff]">Strategic Insights</h3>
              <p className="text-sm text-[#8892b0]">Key findings and recommendations</p>
            </div>
          </div>
          {sectionsExpanded.insights ? (
            <ChevronUp aria-hidden="true" className="h-5 w-5 text-[#5a6a8a]" />
          ) : (
            <ChevronDown aria-hidden="true" className="h-5 w-5 text-[#5a6a8a]" />
          )}
        </button>

        {sectionsExpanded.insights && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="p-6"
          >
            <div className="space-y-3 text-sm text-[#ccd6f6]">
              {outsidePressureData?.executiveSummary?.overallAssessment && (
                <p>• <strong className="text-[#d35400]">External threat environment:</strong> {outsidePressureData.executiveSummary.overallAssessment}</p>
              )}

              {outsidePressureData?.executiveSummary?.highRiskAreas?.length > 0 && (
                <p>• <strong className="text-[#f39c12]">Critical risk areas:</strong> {outsidePressureData.executiveSummary.highRiskAreas.join(', ')}</p>
              )}

              {outsidePressureData?.soWhatAnalysis?.financialImpact && (
                <p>• <strong className="text-[#d35400]">Financial exposure:</strong> {outsidePressureData.soWhatAnalysis.financialImpact.breachCostFormatted} potential breach cost</p>
              )}

              <p>• <strong className="text-[var(--ra-cyber-aqua)]">Readiness position:</strong> Your readiness is <strong>{getReadinessBandLabel(businessData.readinessScore || (100 - businessData.riskScore))}</strong> — {(businessData.readinessScore || (100 - businessData.riskScore)) > (businessData.industryBenchmark?.averageReadiness || 60) ? 'above' : 'below'} the {businessData.industry} industry average</p>

              {outsidePressureData?.executiveSummary?.executiveRecommendation && (
                <p>• <strong className="text-[#7c3aed]">Strategic priority:</strong> {outsidePressureData.executiveSummary.executiveRecommendation}</p>
              )}

              <p>• <strong className="text-[#0d9488]">Recovery capability:</strong> Your {businessData.recoveryDays}-day recovery time is {businessData.recoveryDays < (businessData.industryBenchmark?.averageRecoveryDays || 14) ? 'faster than' : businessData.recoveryDays === (businessData.industryBenchmark?.averageRecoveryDays || 14) ? 'in line with' : 'slower than'} the {businessData.industry} industry average</p>
            </div>
          </motion.div>
        )}
      </div>

      {/* COLLAPSIBLE: External Threat Landscape */}
      <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] overflow-hidden">
        <button
          onClick={() => toggleSection('threats')}
          className="w-full px-6 py-4 flex items-center justify-between bg-[rgba(255,255,255,0.03)] hover:bg-[#172a45] transition-colors"
        >
          <div className="flex items-center">
            <Globe aria-hidden="true" className="h-5 w-5 text-[var(--ra-cyber-aqua)] mr-3" />
            <div className="text-left">
              <h3 className="text-lg font-semibold text-[#e6f1ff]">External Threat Landscape</h3>
              <p className="text-sm text-[#8892b0]">Outside pressures and threat intelligence</p>
            </div>
          </div>
          {sectionsExpanded.threats ? (
            <ChevronUp aria-hidden="true" className="h-5 w-5 text-[#5a6a8a]" />
          ) : (
            <ChevronDown aria-hidden="true" className="h-5 w-5 text-[#5a6a8a]" />
          )}
        </button>

        {sectionsExpanded.threats && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <EnhancedThreatLandscape outsidePressureData={outsidePressureData} industryBenchmark={businessData?.industryBenchmark} />
          </motion.div>
        )}
      </div>
    </div>
  );
};

// ============ INVESTMENT OPTIMIZATION COMPONENT ============
// A failed business-intelligence load says so. It used to be replaced with an invented company
// ("Henry's Hunt Hut", $10M revenue) rendered as if it were the customer's own figures.
const BusinessDataUnavailable: React.FC<{ message: string }> = ({ message }) => (
  <div className="space-y-6">
    <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
      <div className="flex items-center justify-center py-12">
        <AlertCircle aria-hidden="true" className="w-8 h-8 text-[#fbbf24] mr-4" />
        <span className="text-[#a8b2d1]">Business intelligence is unavailable: {message}</span>
      </div>
    </div>
  </div>
);

const InvestmentOptimization: React.FC<{ businessData: BusinessIntelligenceData | null; loading: boolean; outsidePressureData: any; loadError?: string | null }> = ({ businessData, loading, outsidePressureData, loadError }) => {
  // Hooks run before any early return: React requires the same hooks in the same order on every render,
  // and this component returns early while loading, then renders fully once data arrives.
  // State for dynamic optimization parameters
  const [optimizationParams, setOptimizationParams] = useState({
    baseThreatAdjustment: 125000,
    // True only when baseThreatAdjustment was scaled from a real annual revenue — never
    // inferred from the dollar amount itself, since the $25K floor below applies whether
    // or not revenue was measured.
    baseThreatAdjustmentMeasured: false,
    readinessImprovementPerMillion: 10,
    maxReadinessCap: 85,
    threatMultiplier: 1.0,
    outsidePressureScore: 50,
    pressureLevel: 'Medium',
    focusAreas: [],
    investmentPriority: 'Maintain current levels'
  });

  // Set optimization parameters based on business intelligence data
  useEffect(() => {
    if (businessData && outsidePressureData) {
      // Calculate optimization parameters from existing business intelligence data
      const riskScore = businessData.riskScore || 59;
      const outsideScore = outsidePressureData.overallScore || 80;
      const industry = businessData.industry || 'General';

      // Derive parameters from assessment data instead of separate API call.
      // baseAnnualRevenue defaults to 0 only to keep the arithmetic finite — never
      // displayed directly. baseThreatAdjustmentMeasured (below) is what render checks.
      const annualRevenueValue = businessData.annualRevenue;
      const annualRevenueMeasured = annualRevenueValue !== null && annualRevenueValue !== undefined;
      const baseAnnualRevenue = annualRevenueMeasured ? annualRevenueValue : 0;
      const threatAdjustmentPercent = Math.max(0.1, (riskScore / 100) * 0.5); // 0.1-0.5% based on risk
      const calculatedThreatAdjustment = baseAnnualRevenue * (threatAdjustmentPercent / 100);

      setOptimizationParams({
        baseThreatAdjustment: Math.max(25000, calculatedThreatAdjustment), // Minimum $25K, scaled by revenue and risk
        baseThreatAdjustmentMeasured: annualRevenueMeasured,
        readinessImprovementPerMillion: Math.max(5, 15 - (outsideScore / 20)), // 5-15 based on external pressure
        maxReadinessCap: Math.min(95, 85 + (outsideScore / 20)), // 85-95% cap based on threats
        threatMultiplier: 1 + (outsideScore / 200), // 1.0-1.5x multiplier
        outsidePressureScore: outsideScore,
        pressureLevel: outsideScore >= 80 ? 'High' : outsideScore >= 60 ? 'Medium' : 'Low',
        focusAreas: ['Threat Detection', 'Employee Training', 'Compliance'],
        investmentPriority: outsideScore >= 75 ? 'Increase investment urgently' :
                           outsideScore >= 60 ? 'Consider strategic increases' :
                           'Maintain current levels'
      });

      console.log('📊 Investment optimization parameters calculated from business data');
    }
  }, [businessData, outsidePressureData]); // Remove assessmentId dependency since we get it inside the effect

  if (!loading && !businessData && loadError) {
    return <BusinessDataUnavailable message={loadError} />;
  }
  if (loading || !businessData) {
    return (
      <div className="space-y-6">
        <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 aria-hidden="true" className="w-8 h-8 animate-spin text-blue-600 mr-4" />
            <span className="text-[#a8b2d1]">Loading investment analysis...</span>
          </div>
        </div>
      </div>
    );
  }

  // Per-figure suppression, not a whole-screen guard: real non-revenue content (readiness
  // comparisons, focus areas, external-pressure context) must still render for a company
  // whose revenue was never measured. Each revenue-derived figure below is gated
  // individually via revenueMeasured / currentInvestmentMeasured.
  const revenueMeasured = businessData.annualRevenue !== null && businessData.annualRevenue !== undefined;

  const currentReadiness = businessData.readinessScore || (100 - businessData.riskScore);
  const currentRisk = businessData.revenueAtRiskRaw;

  // Use dynamic investment scenarios from API or fallback to calculated ones
  const investmentScenarios = businessData.investmentScenarios || [];

  // Get actual current investment from scenarios (Current Budget scenario)
  const currentBudgetScenario = investmentScenarios.find(s => s.isCurrent || s.label === 'Current Budget');
  const hasRealCurrentBudget = currentBudgetScenario?.investment !== undefined && currentBudgetScenario?.investment !== null;
  // Measured if EITHER a real scenario figure came back from the API OR revenue was
  // measured (the only other input the fallback calculation below can use).
  const currentInvestmentMeasured = hasRealCurrentBudget || revenueMeasured;
  const currentInvestment = hasRealCurrentBudget
    ? currentBudgetScenario.investment
    : (revenueMeasured
        ? (businessData.annualRevenue as number) * ((businessData.industryBenchmark?.recommendedSecurityPercent || 3.5) / 100)
        : 0);

  // Calculate threat-adjusted optimal investment using dynamic parameters
  const threatAdjustmentAmount = Math.round(optimizationParams.baseThreatAdjustment);
  const threatAdjustedInvestment = currentInvestment + threatAdjustmentAmount;
  const threatAdjustedInvestmentMeasured = currentInvestmentMeasured && optimizationParams.baseThreatAdjustmentMeasured;

  // Calculate projected readiness improvement with dynamic parameters
  const threatAdjustedReadiness = Math.min(
    optimizationParams.maxReadinessCap,
    currentReadiness + (threatAdjustmentAmount / 1000000) * optimizationParams.readinessImprovementPerMillion
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Investment Optimization</h1>
            <p className="text-blue-100">When is enough security investment enough?</p>
          </div>
          <TrendingUp aria-hidden="true" className="h-12 w-12 text-blue-200" />
        </div>
      </div>

      {/* Dynamic Parameters Indicator */}
      <div className="ra-glass border border-[rgba(0,217,255,0.25)] rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <TrendingUp aria-hidden="true" className="h-5 w-5 text-[var(--ra-cyber-aqua)]" />
            <span className="text-sm font-medium text-[#e6f1ff]">
              Investment parameters optimized for {businessData.industry} industry
            </span>
          </div>
          <div className="flex items-center space-x-4 text-xs text-[#a8b2d1]">
            <span>Outside Pressure: {getRiskBandLabel(optimizationParams.outsidePressureScore)}</span>
            <span>•</span>
            <span>ROI: {optimizationParams.readinessImprovementPerMillion.toFixed(1)}% per $1M</span>
            <span>•</span>
            <span>Max Readiness: {optimizationParams.maxReadinessCap}%</span>
          </div>
        </div>
      </div>

      {/* Key Insight Banner */}
      <div className="ra-glass border border-[rgba(255,165,0,0.25)] rounded-lg p-6">
        <div className="flex items-start space-x-4">
          <AlertTriangle aria-hidden="true" className="h-6 w-6 text-[var(--ra-warning)] mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-[var(--ra-warning)] mb-2">Critical Insight</h3>
            {revenueMeasured ? (
              <p className="text-[#ccd6f6]">
                <strong>Risk never goes to zero.</strong> At {currentReadiness}% readiness with ${(currentInvestment / 1000000).toFixed(1)}M investment,
                {(() => {
                  const revenue = businessData.annualRevenue as number;
                  const lowThreshold = investmentScenarios[0]?.investment || revenue * 0.01;
                  const midThreshold = investmentScenarios[2]?.investment || revenue * 0.03;
                  const highThreshold = investmentScenarios[3]?.investment || revenue * 0.05;
                  if (currentInvestment < lowThreshold) return " you're critically under-invested. Immediate action required to avoid exponential risk growth.";
                  if (currentInvestment < midThreshold) return ` you're at the entry point of the optimal zone. Consider increasing investment to $${(midThreshold / 1000000).toFixed(1)}M for better risk reduction.`;
                  if (currentInvestment <= highThreshold) return " you're well-positioned in the optimal investment zone with good risk/return balance.";
                  return " you're over-investing with diminishing returns. Consider reallocating funds to other security initiatives.";
                })()}
              </p>
            ) : (
              <NotMeasured needs="annual revenue" />
            )}
          </div>
        </div>
      </div>

      {/* Security Investment Curve - Cyber Poverty Line — entirely revenue-scaled (every
          curve point is annualRevenue * a percentage), so there is no partial version. */}
      {revenueMeasured ? (
        <CyberPovertyLineChart
          annualRevenue={businessData.annualRevenue as number}
          currentInvestment={currentInvestment}
          currentReadiness={currentReadiness}
          industryBenchmark={businessData.industryBenchmark}
          big6UnifiedParams={businessData.advancedAnalytics?.big6UnifiedParams}
        />
      ) : (
        <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
          <h2 className="text-xl font-bold text-[#e6f1ff] mb-4">Security Investment Curve</h2>
          <NotMeasured needs="annual revenue" />
        </div>
      )}

      {/* Investment Scenarios */}
      <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
        <h2 className="text-xl font-bold text-[#e6f1ff] mb-6">Investment Impact Analysis</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {investmentScenarios.map((scenario, index) => (
            <div
              key={index}
              className={`rounded-lg p-6 border ${
                scenario.isCurrent
                  ? 'border-[rgba(0,217,255,0.3)] bg-[rgba(0,217,255,0.06)]'
                  : scenario.label === 'Current/Optimal'
                  ? 'border-[rgba(0,217,255,0.3)] bg-[rgba(0,217,255,0.06)]'
                  : scenario.label === 'Industry Baseline'
                  ? 'border-[rgba(34,197,94,0.3)] bg-[rgba(34,197,94,0.06)]'
                  : scenario.label && scenario.label.includes('Over') && !scenario.roi
                  ? 'border-[rgba(245,158,11,0.3)] bg-[rgba(245,158,11,0.06)]'
                  : scenario.readiness >= 65 && scenario.readiness <= 75 && scenario.roi
                  ? 'border-[rgba(34,197,94,0.3)] bg-[rgba(34,197,94,0.06)]'
                  : scenario.readiness < 55
                  ? 'border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.06)]'
                  : 'border-[rgba(255,255,255,0.12)] ra-glass'
              }`}
            >
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-[#e6f1ff]">
                  {scenario.isCurrent ? 'Current Baseline' :
                   scenario.label === 'Industry Baseline' ? 'Optimized Investment' : scenario.label}
                </h3>
                <div className="text-2xl font-bold text-[var(--ra-cyber-aqua)]">
                  {scenario.investmentFormatted || `$${(scenario.investment / 1000000).toFixed(1)}M`}
                </div>
                <div className="text-sm text-[#a8b2d1]">{scenario.investmentPercent?.toFixed(1)}% of Revenue</div>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-[#a8b2d1]">Readiness</span>
                    <span className="text-sm font-semibold">{getReadinessBandLabel(scenario.readiness)}</span>
                  </div>
                  <div className="w-full bg-[#1e3148] rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        scenario.readiness > (businessData.industryBenchmark?.averageReadiness || 60) ? 'bg-green-500' : 
                        scenario.readiness === (businessData.industryBenchmark?.averageReadiness || 60) ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${scenario.readiness}%` }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-[#a8b2d1]">Revenue at Risk</span>
                    <span className="text-sm font-semibold">{scenario.riskFormatted || `$${(scenario.risk / 1000000).toFixed(1)}M`}</span>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-[#a8b2d1]">
                    {scenario.isCurrent ? 'Status' : 'ROI Assessment'}
                  </div>
                  <div className={`text-sm font-semibold ${
                    scenario.isCurrent ? 'text-[#00d9ff]' :
                    scenario.label === 'Current/Optimal' ? 'text-[#00d9ff]' :
                    scenario.label === 'Industry Baseline' ? 'text-[#22c55e]' :
                    scenario.roi ? 'text-[#22c55e]' : 'text-[#f87171]'
                  }`}>
                    {scenario.isCurrent ? 'Your Starting Point' :
                     scenario.label === 'Current/Optimal' ? 'Optimal Balance' :
                     scenario.label === 'Industry Baseline' ? 'Optimal Investment' :
                     scenario.roi ? 'Positive ROI' : 'Negative ROI'}
                  </div>
                </div>
              </div>

              {scenario.label === 'Current/Optimal' && (
                <div className="mt-4 text-center">
                  <span className="inline-block bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                    Industry Optimal
                  </span>
                </div>
              )}
              
              {scenario.label === 'Industry Baseline' && (
                <div className="mt-4 space-y-2">
                  <div className="text-center">
                    <span className="inline-block bg-green-600 text-white text-xs px-2 py-1 rounded-full">
                      Baseline Optimal
                    </span>
                  </div>
                  <div className="text-xs text-center text-[#a8b2d1]">
                    <AlertTriangle aria-hidden="true" className="inline h-3 w-3 text-amber-500 mr-1" />
                    {optimizationParams.baseThreatAdjustmentMeasured
                      ? `External threats may require +$${(threatAdjustmentAmount / 1000).toFixed(0)}K`
                      : 'External threat adjustment: Not measured (requires annual revenue)'}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Executive Recommendations */}
      <div className="ra-glass rounded-lg p-6 border border-[rgba(0,255,136,0.25)]">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
              <CheckCircle aria-hidden="true" className="w-6 h-6 text-white" />
            </div>
          </div>
          <div>
            <h4 className="text-lg font-semibold text-[#e6f1ff] mb-2">Strategic Recommendations</h4>
            <div className="space-y-2 text-sm text-[#ccd6f6]">
              {revenueMeasured ? (
                <p>{currentInvestment < ((businessData.annualRevenue as number) * 0.028) ? '⚠️' : '✅'} <strong>Current investment level:</strong> ${(currentInvestment / 1000000).toFixed(1)}M ({((currentInvestment / (businessData.annualRevenue as number)) * 100).toFixed(1)}% of revenue) {currentInvestment < ((businessData.annualRevenue as number) * 0.028) ? 'is below' : 'aligns with'} industry recommendations</p>
              ) : (
                <p><strong>Current investment level:</strong> Not measured — requires annual revenue</p>
              )}
              <p>{currentReadiness > (businessData.industryBenchmark?.averageReadiness || 62) ? '✅' : currentReadiness === (businessData.industryBenchmark?.averageReadiness || 62) ? '✅' : '⚠️'} <strong>Readiness position:</strong> {currentReadiness}% readiness is {currentReadiness > (businessData.industryBenchmark?.averageReadiness || 62) ? 'above' : currentReadiness === (businessData.industryBenchmark?.averageReadiness || 62) ? 'equal to' : 'below'} the industry average of {businessData.industryBenchmark?.averageReadiness || 62}%</p>
              <p>{currentReadiness > (businessData.industryBenchmark?.averageReadiness || 62) ? '✅' : currentReadiness === (businessData.industryBenchmark?.averageReadiness || 62) ? '✅' : '⚠️'} <strong>Competitive position:</strong> {currentReadiness > (businessData.industryBenchmark?.averageReadiness || 62) ? 'You outperform industry peers' : currentReadiness === (businessData.industryBenchmark?.averageReadiness || 62) ? 'You match industry standards' : 'Consider improvements to match industry standards'}</p>
              <p>{currentReadiness < 55 || optimizationParams.outsidePressureScore >= 60 ? '⚠️' : '✅'} <strong>Action threshold:</strong> {currentReadiness < 55 ? 'Immediate investment increase recommended' : currentReadiness < (businessData.industryBenchmark?.averageReadiness || 62) ? 'Consider modest investment increase to reach industry average' : optimizationParams.outsidePressureScore >= 60 ? `Readiness is strong but elevated external threats (${optimizationParams.outsidePressureScore.toFixed(0)}%) warrant a targeted increase` : 'Maintain current investment levels'}</p>
              <p>💡 <strong>Focus area:</strong> {currentReadiness < (businessData.industryBenchmark?.averageReadiness || 62) ? 'Prioritize gap closure in high-impact areas' : 'Optimize existing controls for maximum efficiency'}</p>
              {optimizationParams.outsidePressureScore >= 40 && (
              <p>🌍 <strong>External threat context:</strong> {businessData.industry} sector pressure is at {optimizationParams.outsidePressureScore.toFixed(0)}%. {optimizationParams.outsidePressureScore >= 60 ? (threatAdjustedInvestmentMeasured ? `Recommended +$${(threatAdjustmentAmount / 1000).toFixed(0)}K (to $${((currentInvestment + threatAdjustmentAmount) / 1000000).toFixed(1)}M) targeting ${threatAdjustedReadiness.toFixed(0)}% readiness` : 'Recommended increase: Not measured (requires annual revenue)') : 'Current investment is adequate for this threat level'}{optimizationParams.focusAreas.length > 0 ? ` — focus: ${optimizationParams.focusAreas.join(', ')}` : ''}.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ STRATEGIC SCENARIOS COMPONENT ============
const StrategicScenarios: React.FC<{ businessData: BusinessIntelligenceData | null; loading: boolean; loadError?: string | null }> = ({ businessData, loading, loadError }) => {
  const [outsidePressureData, setOutsidePressureData] = useState<any>(null);

  // Sync session-id resolution (URL first; stable for the component's lifetime).
  // Used by TriangulationDrawer to call /api/triangulation/scenarios/:key.
  const [currentSessionId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('sessionId') || params.get('assessmentId');
    if (fromQuery) return fromQuery;
    const pathParts = window.location.pathname.split('/');
    if (pathParts[1] === 'assessment' && pathParts[3] === 'results') return pathParts[2];
    try {
      return localStorage.getItem('currentSessionId');
    } catch {
      return null;
    }
  });

  // Session to show: URL, stored, then the caller's own latest (services/currentSession.ts).
  // null means the viewer has no assessment — never substitute an id.
  const getAssessmentId = (): Promise<string | null> => resolveCurrentSessionId();
  
  // Fetch outside pressure data
  React.useEffect(() => {
    const fetchOutsidePressureData = async () => {
      try {
        const assessmentId = await getAssessmentId();
        if (!assessmentId) return;
        const response = await fetch(apiConfig.riskIntelligence.getOutsidePressures(assessmentId), { headers: authHeaders() });
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.outsidePressures) {
            console.log('Outside Pressure Data loaded:', result.outsidePressures);
            // Include personalizationContext with the outsidePressures data
            const dataWithContext = {
              ...result.outsidePressures,
              personalizationContext: result.personalizationContext
            };
            setOutsidePressureData(dataWithContext);
          }
        }
      } catch (error) {
        console.error('Error fetching outside pressure data:', error);
      }
    };
    
    if (businessData) {
      fetchOutsidePressureData();
    }
  }, [businessData]);

  const [scenarioProbabilities, setScenarioProbabilities] = useState<any>({
    ransomware: { probability: 'Moderate', trend: 'rising', lastIncident: '2 weeks ago', industryIncidents: 3 },
    dataBreach: { probability: 'Low', trend: 'stable', lastIncident: '1 month ago', industryIncidents: 1 },
    supplyChain: { probability: 'High', trend: 'surging', lastIncident: '3 days ago', industryIncidents: 5 },
    otIcs: { probability: 'Moderate', trend: 'rising', lastIncident: '1 week ago', industryIncidents: 2 }
  });

  // Fetch outside pressure data for scenario probabilities
  React.useEffect(() => {
    const fetchScenarioData = async () => {
      try {
        const assessmentId = await getAssessmentId();
        if (!assessmentId) return;
        const response = await fetch(apiConfig.riskIntelligence.getOutsidePressures(assessmentId), { headers: authHeaders() });
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.outsidePressures) {
            // Include personalizationContext with the outsidePressures data
            const dataWithContext = {
              ...result.outsidePressures,
              personalizationContext: result.personalizationContext
            };
            setOutsidePressureData(dataWithContext);
            
            // Update scenario probabilities based on outside pressure data
            const threatScore = result.outsidePressures.overallScore || 65;
            setScenarioProbabilities({
              ransomware: { 
                probability: threatScore > 80 ? 'High' : threatScore > 60 ? 'Moderate' : 'Low',
                trend: 'rising',
                lastIncident: businessData.industry === 'Manufacturing' ? '3 days ago' : '1 week ago',
                industryIncidents: businessData.industry === 'Manufacturing' ? 7 : 4
              },
              dataBreach: {
                probability: threatScore > 75 ? 'Moderate' : 'Low',
                trend: 'stable',
                lastIncident: '2 weeks ago',
                industryIncidents: 2
              },
              supplyChain: {
                probability: threatScore > 70 ? 'High' : 'Moderate',
                trend: 'surging',
                lastIncident: '5 days ago',
                industryIncidents: businessData.industry === 'Manufacturing' ? 8 : 5
              },
              otIcs: {
                probability: businessData.industry === 'Manufacturing' && threatScore > 75 ? 'High' : 'Moderate',
                trend: 'rising',
                lastIncident: '1 week ago',
                industryIncidents: 3
              }
            });

            // Apply PRISM P/D/R maturity adjustments if available
            if (businessData.prismContext?.pdr_maturity) {
              const pdr = businessData.prismContext.pdr_maturity;
              setScenarioProbabilities((prev: any) => ({
                ...prev,
                ransomware: { ...prev.ransomware, probability: pdrAdjustProbability(prev.ransomware.probability, pdr.protect) },
                dataBreach: { ...prev.dataBreach, probability: pdrAdjustProbability(prev.dataBreach.probability, pdr.detect) },
                supplyChain: { ...prev.supplyChain, probability: pdrAdjustProbability(prev.supplyChain.probability, pdr.detect) },
                otIcs: { ...prev.otIcs, probability: pdrAdjustProbability(prev.otIcs.probability, pdr.respond) },
              }));
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch scenario data:', error);
      }
    };
    
    if (businessData) {
      fetchScenarioData();
    }
  }, [businessData]);

  if (!loading && !businessData && loadError) {
    return <BusinessDataUnavailable message={loadError} />;
  }

  if (loading || !businessData) {
    return (
      <div className="space-y-6">
        <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 aria-hidden="true" className="w-8 h-8 animate-spin text-blue-600 mr-4" />
            <span className="text-[#a8b2d1]">Loading scenario analysis...</span>
          </div>
        </div>
      </div>
    );
  }

  // Per-figure suppression, not a whole-screen guard: PRISM mitigation badges, threat
  // probability/trend panels, and every non-revenue bullet below must still render for a
  // company whose revenue was never measured. `revText` gates only the specific
  // dollar-denominated bullets/sentences derived from annualRevenue/dailyRevenue.
  const revenueMeasured = businessData.annualRevenue !== null && businessData.annualRevenue !== undefined;
  const revText = (realText: string): string => revenueMeasured ? realText : 'Not measured';

  // revenueAtRisk is gated on its OWN measured-ness, not revenueMeasured above: the backend
  // nulls it whenever revenue OR employee count is unmeasured, so it can be null even when
  // annualRevenue is a real number. Already carries its own '$' formatting when present.
  const revenueAtRiskText = (businessData.revenueAtRisk === null || businessData.revenueAtRisk === undefined)
    ? 'Not measured'
    : businessData.revenueAtRisk;

  // PRISM deployed mitigations — keyword matching per scenario
  const prism = businessData.prismContext;
  const findMitigations = (keywords: string[]) => {
    if (!prism) return [];
    const matches: Array<{ name: string; source: string; effectiveness?: number }> = [];
    const seen = new Set<string>();
    for (const tool of (prism.deployed_tools || [])) {
      if (keywords.some(kw => tool.name.toLowerCase().includes(kw)) && !seen.has(tool.name)) {
        seen.add(tool.name);
        matches.push({ name: tool.name, source: 'tool' });
      }
    }
    for (const ctrl of (prism.deployed_controls || [])) {
      if (keywords.some(kw => ctrl.name.toLowerCase().includes(kw)) && !seen.has(ctrl.name)) {
        seen.add(ctrl.name);
        matches.push({ name: ctrl.name, source: 'control', effectiveness: ctrl.effectiveness });
      }
    }
    return matches;
  };

  const scenarioMitigations = prism ? {
    ransomware: findMitigations(['backup', 'endpoint', 'edr', 'crowdstrike', 'sentinelone', 'email', 'antimalware', 'anti-malware', 'immutable']),
    dataBreach: findMitigations(['dlp', 'encryption', 'access', 'iam', 'siem', 'splunk', 'mfa', 'identity', 'zero trust']),
    supplyChain: findMitigations(['vendor', 'third-party', 'sbom', 'supply chain', 'monitoring', 'risk management']),
    otIcs: findMitigations(['scada', 'ics', 'ot', 'industrial', 'plc', 'segmentation', 'purdue']),
  } : null;

  // Adjust scenario probabilities based on PRISM P/D/R maturity
  const pdrAdjustProbability = (baseProbability: string, relevantScore: number | null | undefined): string => {
    if (relevantScore == null || relevantScore < 2.5) return baseProbability;
    // Strong maturity (≥2.5/4) downgrades probability one level
    if (baseProbability === 'High') return 'Moderate';
    if (baseProbability === 'Moderate') return 'Low';
    return baseProbability;
  };

  const renderMitigationBadges = (mitigations: Array<{ name: string; source: string; effectiveness?: number }>) => {
    if (!mitigations || mitigations.length === 0) return null;
    return (
      <div className="mb-4 p-3 rounded" style={{ background: 'rgba(0, 255, 136, 0.08)', border: '1px solid rgba(0, 255, 136, 0.30)' }}>
        <p className="text-sm font-medium mb-2" style={{ color: '#86efac' }}>
          <Shield aria-hidden="true" className="w-4 h-4 inline mr-1" /> Deployed Mitigations (from PRISM)
        </p>
        <div className="flex flex-wrap gap-2">
          {mitigations.map((m, i) => (
            <span key={i} className="px-2 py-1 text-xs rounded-full"
              style={{ background: 'rgba(0, 255, 136, 0.12)', color: '#bbf7d0', border: '1px solid rgba(0, 255, 136, 0.35)' }}>
              {m.name}{m.effectiveness ? ` (${m.effectiveness}/5)` : ''}
            </span>
          ))}
        </div>
      </div>
    );
  };

  // 0 only keeps the arithmetic below finite when unmeasured — every display site routes
  // through revText/revenueMeasured, never this raw value.
  const annualRevenue = revenueMeasured ? (businessData.annualRevenue as number) : 0;
  const dailyRevenue = annualRevenue / 365;
  const readiness = businessData.readinessScore || (100 - businessData.riskScore);
  const baseRecoveryDays = businessData.recoveryDays;
  // Scenario-specific recovery multipliers — different incidents have different restoration timelines
  const recoveryDays = {
    ransomware: Math.round(baseRecoveryDays * 1.0),       // Base case — encrypted systems, full restore
    dataBreach: Math.round(baseRecoveryDays * 0.5),       // Faster — investigation-based, systems stay up
    supplyChain: Math.round(baseRecoveryDays * 1.5),      // Longer — vendor verification, alternate sourcing
    otIcs: Math.round(baseRecoveryDays * 2.0),            // Longest — OT systems require careful staged restoration
    insiderThreat: Math.round(baseRecoveryDays * 0.75),   // Moderate — targeted remediation, access revocation
  };
  const riskScore = businessData.riskScore || 59;

  // Revenue-scaled loss calculations (replaces hardcoded dollar ranges)
  const fmt = (v: number) => v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : `$${(v / 1000).toFixed(0)}K`;
  const losses = {
    ransomware: {
      min: annualRevenue * 0.02,
      likely: annualRevenue * 0.05,
      max: annualRevenue * 0.12,
      ransom: annualRevenue * 0.005,       // ~0.5% of revenue
      ransomMax: annualRevenue * 0.01,
      recovery: annualRevenue * 0.02,
      recoveryMax: annualRevenue * 0.04,
    },
    dataBreach: {
      min: annualRevenue * 0.01,
      likely: annualRevenue * 0.03,
      max: annualRevenue * 0.08,
      direct: annualRevenue * 0.01,
      directMax: annualRevenue * 0.02,
      notification: annualRevenue * 0.002,
      notificationMax: annualRevenue * 0.003,
      legal: annualRevenue * 0.005,
      legalMax: annualRevenue * 0.012,
      monitoring: annualRevenue * 0.003,
      monitoringMax: annualRevenue * 0.005,
    },
    supplyChain: {
      min: annualRevenue * 0.015,
      likely: annualRevenue * 0.04,
      max: annualRevenue * 0.10,
    },
    otIcs: {
      min: annualRevenue * 0.04,
      likely: annualRevenue * 0.07,
      max: annualRevenue * 0.15,
      equipment: annualRevenue * 0.005,
      equipmentMax: annualRevenue * 0.02,
    },
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-red-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Strategic Scenarios</h1>
            <p className="text-purple-100">What are the business implications if something happens?</p>
          </div>
          <Target aria-hidden="true" className="h-12 w-12 text-purple-200" />
        </div>
      </div>

      {/* Key Message Banner */}
      <div className="rounded-lg p-6" style={{ background: 'rgba(0, 217, 255, 0.06)', border: '1px solid rgba(0, 217, 255, 0.30)' }}>
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(0, 217, 255, 0.20)', border: '1px solid rgba(0, 217, 255, 0.50)' }}>
              <BarChart3 aria-hidden="true" className="w-6 h-6" style={{ color: '#7dd3fc' }} />
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-[#e6f1ff] mb-2">Business Impact Translator</h3>
            <p className="text-[#ccd6f6]">
              While your security team optimizes controls, these scenarios translate cyber risks into business language.
              <strong> This is what cyber incidents mean for {businessData.companyName}'s operations, customers, and bottom line.</strong>
            </p>
            <div className="mt-3 p-3 rounded-lg" style={{ background: 'rgba(0, 217, 255, 0.10)', border: '1px solid rgba(0, 217, 255, 0.25)' }}>
              <p className="text-sm text-[#e6f1ff]">
                <strong>Risk Metrics Explained:</strong><br/>
                • <strong>Revenue at Risk ({revenueAtRiskText}):</strong> Worst-case scenario - catastrophic incident with maximum business disruption<br/>
                • <strong>Likely Incident Loss ({revText(`${fmt(losses.ransomware.likely)}-${fmt(losses.ransomware.max)}`)}):</strong> Typical ransomware or breach scenario scaled to your revenue<br/>
                • <strong>Expected Annual Loss:</strong> Potentiality-weighted loss expectancy across all threat scenarios
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Incident Scenarios */}
      <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
        <h2 className="text-xl font-bold text-[#e6f1ff] mb-6">Major Incident Scenarios</h2>
        <div className="space-y-8">
          
          {/* Manufacturing-Specific: OT/ICS Attack (only show for manufacturing) */}
          {businessData.industry === 'Manufacturing' && (
            <div className="rounded-lg p-6" style={{ background: 'rgba(168, 85, 247, 0.08)', border: '1px solid rgba(168, 85, 247, 0.30)' }}>
              <div className="flex items-center mb-4">
                <Zap aria-hidden="true" className="h-8 w-8 mr-3" style={{ color: '#c4b5fd' }} />
                <h3 className="text-xl font-semibold text-[#e6f1ff]">OT/ICS System Attack</h3>
                <span className="ml-3 px-2 py-1 text-xs rounded-full" style={{ background: 'rgba(168, 85, 247, 0.20)', color: '#e9d5ff', border: '1px solid rgba(168, 85, 247, 0.40)' }}>Manufacturing Specific</span>
              </div>
              <div className="mb-4 p-3 ra-glass rounded border">
                <strong>Industry Context:</strong> {scenarioProbabilities.oticsAttack?.industryIncidents || '12 OT/ICS attacks'} in {businessData.industry} sector • Average cost: {revText(`${fmt(losses.otIcs.min)}-${fmt(losses.otIcs.max)}`)} •
                Outside Pressure: {getRiskBandLabel(outsidePressureData?.categoryScores?.['Technological Advancements']?.score || 0)}
              </div>
              {scenarioMitigations && renderMitigationBadges(scenarioMitigations.otIcs)}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h4 className="font-semibold text-[#e6f1ff] mb-2">Production Impact</h4>
                  <ul className="text-sm text-[#ccd6f6] space-y-1">
                    <li>• <strong>Immediate halt:</strong> Complete production shutdown</li>
                    <li>• <strong>Revenue loss:</strong> {revText(`$${(dailyRevenue * recoveryDays.otIcs / 1000000).toFixed(1)}M`)} ({recoveryDays.otIcs} days to rebuild OT)</li>
                    <li>• <strong>Safety systems:</strong> Emergency protocols activated</li>
                    <li>• <strong>Equipment damage:</strong> Potential {revText(`${fmt(losses.otIcs.equipment)}-${fmt(losses.otIcs.equipmentMax)}`)} in repairs</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-[#e6f1ff] mb-2">Operational Recovery</h4>
                  <ul className="text-sm text-[#ccd6f6] space-y-1">
                    <li>• <strong>Day 1-7:</strong> Complete operational lockdown</li>
                    <li>• <strong>Day 8-{Math.min(14, recoveryDays.otIcs)}:</strong> Manual operations, 15% capacity</li>
                    <li>• <strong>Day {Math.min(15, recoveryDays.otIcs)}+:</strong> Phased ICS restoration</li>
                    <li>• <strong>Full recovery:</strong> {recoveryDays.otIcs}-day timeline</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-[#e6f1ff] mb-2">Strategic Implications</h4>
                  <ul className="text-sm text-[#ccd6f6] space-y-1">
                    <li>• <strong>Customer orders:</strong> 3-4 week backlog</li>
                    <li>• <strong>Market position:</strong> Competitors gain shelf space</li>
                    <li>• <strong>Regulatory:</strong> IEC 62443 compliance review</li>
                    <li>• <strong>Workforce:</strong> Temporary layoffs during rebuild</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Ransomware Scenario */}
          <div className="rounded-lg p-6" style={{ background: 'rgba(220, 38, 38, 0.08)', border: '1px solid rgba(220, 38, 38, 0.30)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <AlertTriangle aria-hidden="true" className="h-8 w-8 mr-3" style={{ color: '#fca5a5' }} />
                <h3 className="text-xl font-semibold text-[#e6f1ff]">Ransomware Attack</h3>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  scenarioProbabilities.ransomware.probability === 'High' ? 'bg-red-600 text-white' :
                  scenarioProbabilities.ransomware.probability === 'Moderate' ? 'bg-orange-500 text-white' :
                  'bg-yellow-500 text-white'
                }`}>
                  {scenarioProbabilities.ransomware.probability} Potentiality
                </span>
                <span className="text-sm flex items-center" style={{ color: '#fca5a5' }}>
                  {scenarioProbabilities.ransomware.trend === 'surging' ? '↑↑' :
                   scenarioProbabilities.ransomware.trend === 'rising' ? '↑' : '→'} Trending {scenarioProbabilities.ransomware.trend}
                </span>
              </div>
            </div>
            <div className="mb-4 p-3 rounded-lg" style={{ background: 'rgba(220, 38, 38, 0.12)', border: '1px solid rgba(220, 38, 38, 0.25)' }}>
              <p className="text-sm text-[#ccd6f6]">
                <strong>Industry Context:</strong> {scenarioProbabilities.ransomware.industryIncidents} ransomware attacks in {businessData.industry} sector •
                Average cost: {revText(`${fmt(losses.ransomware.likely)}-${fmt(losses.ransomware.max)}`)} •
                Outside Pressure: {getRiskBandLabel(outsidePressureData?.categoryScores?.['Evolving Threat Landscape']?.score || 0)}
              </p>
            </div>
            {scenarioMitigations && renderMitigationBadges(scenarioMitigations.ransomware)}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-semibold text-[#e6f1ff] mb-2">Financial Impact</h4>
                <ul className="text-sm text-[#ccd6f6] space-y-1">
                  {/* The day count here is not revenue-derived — keep it rendering even when
                      the dollar figure suppresses, instead of losing it inside a wholesale swap. */}
                  <li>• <strong>Revenue loss:</strong> {revText(`$${(dailyRevenue * recoveryDays.ransomware / 1000000).toFixed(1)}M`)} ({recoveryDays.ransomware} days downtime)</li>
                </ul>
                {revenueMeasured ? (
                  <ul className="text-sm text-[#ccd6f6] space-y-1">
                    <li>• <strong>Ransom demand:</strong> {fmt(losses.ransomware.ransom)}-{fmt(losses.ransomware.ransomMax)} typical for {businessData.industry}</li>
                    <li>• <strong>Recovery costs:</strong> {fmt(losses.ransomware.recovery)}-{fmt(losses.ransomware.recoveryMax)} (forensics, rebuilding, legal)</li>
                    <li>• <strong>Total impact:</strong> {fmt(losses.ransomware.likely)}-{fmt(losses.ransomware.max)} potential loss</li>
                  </ul>
                ) : (
                  <NotMeasured needs="annual revenue" />
                )}
              </div>
              <div>
                <h4 className="font-semibold text-[#e6f1ff] mb-2">Operational Impact</h4>
                <ul className="text-sm text-[#ccd6f6] space-y-1">
                  <li>• <strong>Day 1-3:</strong> Complete operations halt</li>
                  <li>• <strong>Day 4-8:</strong> Manual processes, 30% capacity</li>
                  <li>• <strong>Day 9+:</strong> Gradual system restoration</li>
                  <li>• <strong>Customer impact:</strong> Service disruption, order delays</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-[#e6f1ff] mb-2">Strategic Consequences</h4>
                <ul className="text-sm text-[#ccd6f6] space-y-1">
                  <li>• <strong>Market share:</strong> Competitors gain during downtime</li>
                  <li>• <strong>Customer trust:</strong> 6-month recovery period</li>
                  <li>• <strong>Regulatory:</strong> Potential fines, compliance issues</li>
                  <li>• <strong>Insurance:</strong> Premium increases, coverage gaps</li>
                </ul>
              </div>
            </div>
            <TriangulationDrawer
              sessionId={currentSessionId}
              scenarioKey="ransomware"
              scenarioLabel="Ransomware Attack"
            />
          </div>

          {/* Data Breach Scenario */}
          <div className="rounded-lg p-6" style={{ background: 'rgba(249, 115, 22, 0.08)', border: '1px solid rgba(249, 115, 22, 0.30)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Shield aria-hidden="true" className="h-8 w-8 mr-3" style={{ color: '#fdba74' }} />
                <h3 className="text-xl font-semibold text-[#e6f1ff]">Data Breach</h3>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  scenarioProbabilities.dataBreach.probability === 'High' ? 'bg-red-600 text-white' :
                  scenarioProbabilities.dataBreach.probability === 'Moderate' ? 'bg-orange-500 text-white' :
                  'bg-green-500 text-white'
                }`}>
                  {scenarioProbabilities.dataBreach.probability} Potentiality
                </span>
                <span className="text-sm" style={{ color: '#fdba74' }}>
                  {scenarioProbabilities.dataBreach.trend === 'surging' ? '↑↑' :
                   scenarioProbabilities.dataBreach.trend === 'rising' ? '↑' : '→'} {scenarioProbabilities.dataBreach.trend}
                </span>
              </div>
            </div>
            <div className="mb-4 p-3 rounded-lg" style={{ background: 'rgba(249, 115, 22, 0.12)', border: '1px solid rgba(249, 115, 22, 0.25)' }}>
              <p className="text-sm text-[#ccd6f6]">
                <strong>Industry Context:</strong> {scenarioProbabilities.dataBreach.industryIncidents} breaches in {businessData.industry} sector •
                Average cost: {revText(fmt(losses.dataBreach.likely))} •
                Outside Pressure: {getRiskBandLabel(outsidePressureData?.categoryScores?.['Regulatory & Compliance']?.score || 0)}
              </p>
            </div>
            {scenarioMitigations && renderMitigationBadges(scenarioMitigations.dataBreach)}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-semibold text-[#e6f1ff] mb-2">Financial Impact</h4>
                {revenueMeasured ? (
                  <ul className="text-sm text-[#ccd6f6] space-y-1">
                    <li>• <strong>Direct costs:</strong> {fmt(losses.dataBreach.direct)}-{fmt(losses.dataBreach.directMax)} (average for {businessData.industry})</li>
                    <li>• <strong>Notification costs:</strong> {fmt(losses.dataBreach.notification)}-{fmt(losses.dataBreach.notificationMax)} (customer/regulatory)</li>
                    <li>• <strong>Legal defense:</strong> {fmt(losses.dataBreach.legal)}-{fmt(losses.dataBreach.legalMax)} (class action, regulatory)</li>
                    <li>• <strong>Credit monitoring:</strong> {fmt(losses.dataBreach.monitoring)}-{fmt(losses.dataBreach.monitoringMax)} for affected customers</li>
                  </ul>
                ) : (
                  <NotMeasured needs="annual revenue" />
                )}
              </div>
              <div>
                <h4 className="font-semibold text-[#e6f1ff] mb-2">Business Disruption</h4>
                <ul className="text-sm text-[#ccd6f6] space-y-1">
                  <li>• <strong>Investigation period:</strong> 2-4 weeks limited operations</li>
                  <li>• <strong>Customer onboarding:</strong> Halted during investigation</li>
                  <li>• <strong>System lockdown:</strong> Limited access to prevent further exposure</li>
                  <li>• <strong>Executive time:</strong> 40+ hours/week for 3 months</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-[#e6f1ff] mb-2">Long-term Effects</h4>
                <ul className="text-sm text-[#ccd6f6] space-y-1">
                  <li>• <strong>Customer churn:</strong> 15-25% in first year</li>
                  <li>• <strong>Acquisition cost:</strong> 3x higher for 18 months</li>
                  <li>• <strong>Regulatory oversight:</strong> 2-3 years enhanced scrutiny</li>
                  <li>• <strong>Stock impact:</strong> 5-10% drop (if public)</li>
                </ul>
              </div>
            </div>
            <TriangulationDrawer
              sessionId={currentSessionId}
              scenarioKey="breach"
              scenarioLabel="Data Breach"
            />
          </div>

          {/* Supply Chain Disruption */}
          <div className="rounded-lg p-6" style={{ background: 'rgba(234, 179, 8, 0.08)', border: '1px solid rgba(234, 179, 8, 0.30)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Globe aria-hidden="true" className="h-8 w-8 mr-3" style={{ color: '#fde047' }} />
                <h3 className="text-xl font-semibold text-[#e6f1ff]">Supply Chain Cyber Attack</h3>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  scenarioProbabilities.supplyChain.probability === 'High' ? 'bg-red-600 text-white' :
                  scenarioProbabilities.supplyChain.probability === 'Moderate' ? 'bg-orange-500 text-white' :
                  'bg-yellow-500 text-white'
                }`}>
                  {scenarioProbabilities.supplyChain.probability} Potentiality
                </span>
                <span className="text-sm font-medium" style={{ color: '#fde047' }}>
                  {scenarioProbabilities.supplyChain.trend === 'surging' ? '↑↑ SURGING' :
                   scenarioProbabilities.supplyChain.trend === 'rising' ? '↑ Rising' : '→ Stable'}
                </span>
              </div>
            </div>
            <div className="mb-4 p-3 rounded-lg" style={{ background: 'rgba(234, 179, 8, 0.12)', border: '1px solid rgba(234, 179, 8, 0.25)' }}>
              <p className="text-sm text-[#ccd6f6]">
                <strong>Industry Context:</strong> {scenarioProbabilities.supplyChain.industryIncidents} supply chain attacks in {businessData.industry} sector •
                Average cost: {revText(`${fmt(losses.supplyChain.likely)}-${fmt(losses.supplyChain.max)}`)} •
                Outside Pressure: {getRiskBandLabel(outsidePressureData?.categoryScores?.['Supply Chain Vulnerabilities']?.score || 0)}
              </p>
            </div>
            {scenarioMitigations && renderMitigationBadges(scenarioMitigations.supplyChain)}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-semibold text-[#e6f1ff] mb-2">Immediate Impact</h4>
                <ul className="text-sm text-[#ccd6f6] space-y-1">
                  <li>• <strong>Production halt:</strong> {Math.max(3, Math.round(recoveryDays.supplyChain * 0.3))}-{Math.max(7, Math.round(recoveryDays.supplyChain * 0.5))} days while securing systems</li>
                  <li>• <strong>Revenue loss:</strong> {revText(`$${(dailyRevenue * recoveryDays.supplyChain / 1000000).toFixed(1)}M`)} ({recoveryDays.supplyChain}-day recovery)</li>
                  <li>• <strong>Emergency sourcing:</strong> 25-40% cost premium</li>
                  <li>• <strong>Vendor investigation:</strong> {revText(`${fmt(annualRevenue * 0.001)}-${fmt(annualRevenue * 0.003)}`)} costs</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-[#e6f1ff] mb-2">Operational Challenges</h4>
                <ul className="text-sm text-[#ccd6f6] space-y-1">
                  <li>• <strong>Alternative suppliers:</strong> 30-60 day onboarding</li>
                  <li>• <strong>Quality control:</strong> New vendor validation required</li>
                  <li>• <strong>Customer commitments:</strong> Delivery delays, penalties</li>
                  <li>• <strong>Inventory management:</strong> Emergency stock buildout</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-[#e6f1ff] mb-2">Strategic Response</h4>
                <ul className="text-sm text-[#ccd6f6] space-y-1">
                  <li>• <strong>Vendor diversification:</strong> Accelerate dual-sourcing strategy</li>
                  <li>• <strong>Contract renegotiation:</strong> Enhanced security requirements</li>
                  <li>• <strong>Customer communication:</strong> Proactive transparency program</li>
                  <li>• <strong>Competitive positioning:</strong> Market differentiation opportunity</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Competitive Intelligence */}
      <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
        <h2 className="text-xl font-bold text-[#e6f1ff] mb-6">Competitive Scenario Intelligence</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-lg p-6" style={{ background: 'rgba(0, 255, 136, 0.06)', border: '1px solid rgba(0, 255, 136, 0.30)' }}>
            <div className="flex items-center mb-4">
              <TrendingUp aria-hidden="true" className="h-6 w-6 mr-3" style={{ color: '#86efac' }} />
              <h3 className="text-lg font-semibold text-[#e6f1ff]">If Competitors Get Hit First</h3>
            </div>
            <ul className="text-sm text-[#ccd6f6] space-y-2">
              <li>• <strong>Market opportunity:</strong> Capture 10-15% additional market share during their recovery</li>
              <li>• <strong>Customer migration:</strong> Proactive outreach to displaced customers</li>
              <li>• <strong>Competitive advantage:</strong> "Security as differentiator" messaging</li>
              <li>• <strong>Revenue upside:</strong> {revText(`${fmt(annualRevenue * 0.10)}-${fmt(annualRevenue * 0.15)}`)} potential in first year</li>
            </ul>
          </div>
          <div className="rounded-lg p-6" style={{ background: 'rgba(220, 38, 38, 0.08)', border: '1px solid rgba(220, 38, 38, 0.30)' }}>
            <div className="flex items-center mb-4">
              <TrendingDown aria-hidden="true" className="h-6 w-6 mr-3" style={{ color: '#fca5a5' }} />
              <h3 className="text-lg font-semibold text-[#e6f1ff]">If We're the Only Target</h3>
            </div>
            <ul className="text-sm text-[#ccd6f6] space-y-2">
              <li>• <strong>Competitive disadvantage:</strong> Competitors capture our customers during downtime</li>
              <li>• <strong>Market perception:</strong> "Least secure option" reputation</li>
              <li>• <strong>Recovery challenge:</strong> Harder to rebuild with active competition</li>
              <li>• <strong>Strategic risk:</strong> Potential acquisition target at discount</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Executive Decision Framework */}
      <div className="ra-glass rounded-lg p-6 border border-[rgba(99,102,241,0.25)]">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center">
              <Target aria-hidden="true" className="w-6 h-6 text-white" />
            </div>
          </div>
          <div>
            <h4 className="text-lg font-semibold text-[#e6f1ff] mb-2">Executive Decision Framework</h4>
            <div className="space-y-2 text-sm text-[#ccd6f6]">
              <p>🎯 <strong>Current Position:</strong> Your readiness is <strong>{getReadinessBandLabel(readiness)}</strong> — {readiness >= (businessData.industryBenchmark?.averageReadiness || 62) ? 'exceeding' : 'approaching'} the {businessData.industry} industry average</p>
              <p>📊 <strong>Risk-Adjusted Exposure:</strong> Likely incident loss of $15-25M (typical ransomware) vs {revenueMeasured ? `$${((annualRevenue * (businessData.industryBenchmark?.recommendedSecurityPercent || 3.2) / 100) / 1000000).toFixed(0)}M annual security investment = ${readiness >= 60 ? 'acceptable' : 'needs improvement'} risk/reward ratio` : 'Not measured — requires annual revenue'}</p>
              <p>🏆 <strong>Competitive Position:</strong> {readiness >= (businessData.industryBenchmark?.averageReadiness || 62) ? 'Well positioned to gain market share during industry incidents' : 'Build readiness to capitalize on competitor incidents'}</p>
              <p>⚖️ <strong>Strategic Recommendation:</strong> {readiness >= (businessData.industryBenchmark?.averageReadiness || 62) ? 'Current security posture appropriate - focus on incident response optimization' : 'Increase investment to reach industry parity and reduce incident exposure'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ BOARD REPORTS COMPONENT ============
const BoardReports: React.FC<{ businessData: BusinessIntelligenceData | null; loading: boolean; loadError?: string | null }> = ({ businessData, loading, loadError }) => {
  const boardReportRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  const handleExportPDF = async () => {
    if (!boardReportRef.current || exporting) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(boardReportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#0a192f',
        logging: false,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 10;

      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= (pageHeight - 20);

      while (heightLeft > 0) {
        position = -(pageHeight - 20) * (Math.ceil((imgHeight - heightLeft) / (pageHeight - 20)));
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position + 10, imgWidth, imgHeight);
        heightLeft -= (pageHeight - 20);
      }

      const date = new Date().toISOString().split('T')[0];
      pdf.save(`Board_Report_${date}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  if (!loading && !businessData && loadError) {
    return <BusinessDataUnavailable message={loadError} />;
  }

  if (loading || !businessData) {
    return (
      <div className="space-y-6">
        <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 aria-hidden="true" className="w-8 h-8 animate-spin text-blue-600 mr-4" />
            <span className="text-[#a8b2d1]">Loading board analytics...</span>
          </div>
        </div>
      </div>
    );
  }

  const annualRevenue = businessData.annualRevenue;
  const revenueAtRisk = businessData.revenueAtRiskRaw;
  const industry = businessData.industry;
  const riskScore = businessData.riskScore;
  const recoveryDays = businessData.recoveryDays;
  // Task 11 follow-up (2026-09-22): revenueAtRiskRaw / annualRevenue can now genuinely
  // arrive null (an unmeasured company), not just a fabricated 0 — this component never
  // gated on that. Every figure below that reads either one is gated on these flags rather
  // than trusting raw arithmetic, so an unmeasured session degrades honestly instead of
  // asserting a confident $0.
  const revenueAtRiskMeasured = revenueAtRisk !== null && revenueAtRisk !== undefined;
  const revenueMeasured = annualRevenue !== null && annualRevenue !== undefined;

  // Calculate dynamic insurance gap based on actual assessment data
  const calculateInsuranceGap = (): {
    gap: number | null;
    effectiveCoverage: number | null;
    coveragePercentage: number | null;
    hasPolicy: boolean;
  } => {
    // Get actual cyber insurance coverage from assessment
    const cyberInsurance = businessData.cyberInsurance || {
      coverageLimit: 0,
      perIncidentLimit: 0,
      ransomwareCoverage: 0
    };
    const hasPolicy = (cyberInsurance.coverageLimit || 0) > 0;

    if (!revenueAtRiskMeasured) {
      return { gap: null, effectiveCoverage: null, coveragePercentage: null, hasPolicy };
    }

    // Calculate actual coverage vs potential loss
    const totalPotentialCoverage = Math.min(
      cyberInsurance.coverageLimit || 0,
      cyberInsurance.perIncidentLimit || cyberInsurance.coverageLimit || 0
    );
    const effectiveCoverage = Math.min(totalPotentialCoverage, revenueAtRisk);
    const coveragePercentage = revenueAtRisk > 0 ? effectiveCoverage / revenueAtRisk : 0;

    // Insurance gap is what's NOT covered
    const gap = revenueAtRisk - effectiveCoverage;

    console.log('🛡️ Insurance Coverage Analysis:', {
      totalPolicy: cyberInsurance.coverageLimit,
      perIncident: cyberInsurance.perIncidentLimit,
      revenueAtRisk,
      effectiveCoverage,
      coveragePercentage: (coveragePercentage * 100).toFixed(1) + '%',
      insuranceGap: gap
    });

    return { gap, effectiveCoverage, coveragePercentage, hasPolicy };
  };

  const insuranceCoverage = calculateInsuranceGap();
  const insuranceGap = insuranceCoverage.gap;
  const annualRiskBudget = revenueMeasured ? (annualRevenue as number) * 0.035 : null; // 3.5% industry benchmark
  // Competitive impact: base 5% per month of disruption, scaled by risk score severity
  const riskSeverityMultiplier = riskScore > 70 ? 1.5 : riskScore > 50 ? 1.0 : 0.7;
  const competitorAdvantage = (recoveryDays / 30) * 0.05 * riskSeverityMultiplier;
  
  // Regulatory penalty calculations — derived from revenue and industry regulatory pressure.
  // Guard review F1 (2026-09-22): this was reachable in the exact render path the Task 11
  // fix hardened, computing Math.min(annualRevenue * rate, cap) with no null guard — a
  // confident "$0 — Maximum penalty" for an unmeasured revenue. Gated on revenueMeasured
  // (defined above, alongside every other revenue-derived figure in this component).
  const getRegulatoryPenalties = (): { framework: string; maxPenalty: number | null; avgPenalty: number | null } => {
    const regulatoryPressure = businessData.industryBenchmark?.regulatoryPressure || 'Standard';
    const complianceFrameworks = businessData.industryBenchmark?.complianceFrameworks || [];
    const framework = complianceFrameworks.length > 0
      ? complianceFrameworks.slice(0, 2).join(' / ')
      : (businessData.industryBenchmark?.focusAreas?.[0] || 'General Compliance');

    if (!revenueMeasured) {
      return { framework, maxPenalty: null, avgPenalty: null };
    }

    // Penalty rates scale with regulatory pressure and revenue
    const pressureRates: Record<string, { maxRate: number; avgRate: number; cap: number }> = {
      'High':     { maxRate: 0.045, avgRate: 0.020, cap: 75000000 },
      'Moderate': { maxRate: 0.030, avgRate: 0.012, cap: 25000000 },
      'Standard': { maxRate: 0.020, avgRate: 0.008, cap: 15000000 }
    };
    const rates = pressureRates[regulatoryPressure] || pressureRates['Standard'];

    return {
      framework,
      maxPenalty: Math.min((annualRevenue as number) * rates.maxRate, rates.cap),
      avgPenalty: Math.min((annualRevenue as number) * rates.avgRate, rates.cap * 0.5)
    };
  };

  const regulatoryInfo = getRegulatoryPenalties();
  
  // Format currency for board presentation. amount is null when the underlying figure was
  // never measured — that used to be impossible here (revenueAtRiskRaw always arrived
  // defaulted to 0), so this threw on `null.toLocaleString()` the moment the producer-side
  // fix let a real null through. "Not measured" beats both the crash and a fabricated $0.
  const formatBoardCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) {
      return 'Not measured';
    }
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    } else {
      return `$${amount.toLocaleString()}`;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 to-blue-900 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Board Reports</h1>
            <p className="text-blue-100">Executive governance and financial risk intelligence</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportPDF}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-50"
            >
              <Download aria-hidden="true" className="w-4 h-4" />
              {exporting ? 'Exporting...' : 'Export PDF'}
            </button>
            <FileText aria-hidden="true" className="h-12 w-12 text-blue-200" />
          </div>
        </div>
      </div>

      {/* Broker placement (cyber-insurance) — renders only when the backend emits the
          flag-gated `placement` block (BROKER_PLACEMENT_ENABLED). Self-contained light panel. */}
      {businessData.placement && (
        <BrokerPlacementCard
          placement={businessData.placement}
          companyName={businessData.companyName}
        />
      )}

      {/* Executive Summary Dashboard */}
      <div ref={boardReportRef}>
      <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
        <h2 className="text-xl font-bold text-[#e6f1ff] mb-6">Executive Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="rounded-lg p-4" style={{ background: 'rgba(220, 38, 38, 0.10)', border: '1px solid rgba(220, 38, 38, 0.30)' }}>
            <div className="flex items-center">
              <AlertTriangle aria-hidden="true" className="h-8 w-8 mr-3" style={{ color: '#fca5a5' }} />
              <div>
                <p className="text-sm font-medium" style={{ color: '#fca5a5' }}>Maximum Exposure</p>
                <p className="text-2xl font-bold text-[#e6f1ff]">{formatBoardCurrency(revenueAtRisk)}</p>
              </div>
            </div>
          </div>
          
          <div className={`rounded-lg p-4 ${insuranceGap === null ? 'bg-[rgba(90,106,138,0.1)] border border-[rgba(90,106,138,0.3)]' : insuranceGap > 0 ? 'bg-[rgba(245,158,11,0.1)] border border-[rgba(245,158,11,0.3)]' : 'bg-[rgba(34,197,94,0.1)] border border-[rgba(34,197,94,0.3)]'}`}>
            <div className="flex items-center">
              <Shield aria-hidden="true" className={`h-8 w-8 mr-3 ${insuranceGap === null ? 'text-[#5a6a8a]' : insuranceGap > 0 ? 'text-[#fbbf24]' : 'text-[#22c55e]'}`} />
              <div>
                <p className={`text-sm font-medium ${insuranceGap === null ? 'text-[#5a6a8a]' : insuranceGap > 0 ? 'text-[#fbbf24]' : 'text-[#22c55e]'}`}>Insurance Gap</p>
                <p className="text-2xl font-bold text-[#e6f1ff]">{formatBoardCurrency(insuranceGap)}</p>
              </div>
            </div>
          </div>
          
          <div className="rounded-lg p-4" style={{ background: 'rgba(0, 217, 255, 0.08)', border: '1px solid rgba(0, 217, 255, 0.30)' }}>
            <div className="flex items-center">
              <TrendingUp aria-hidden="true" className="h-8 w-8 mr-3" style={{ color: '#7dd3fc' }} />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium" style={{ color: '#7dd3fc' }}>Annual Risk Budget</p>
                  <HelpTooltip
                    title="Annual Risk Budget"
                    content={`Calculated as ${((businessData.industryBenchmark?.recommendedSecurityPercent || 3.5)).toFixed(1)}% of annual revenue (${formatBoardCurrency(businessData.annualRevenue)} × ${((businessData.industryBenchmark?.recommendedSecurityPercent || 3.5)).toFixed(1)}% = ${formatBoardCurrency(annualRiskBudget)}) - industry benchmark for ${businessData.industry || 'this sector'}`}
                    side="right"
                  />
                </div>
                <p className="text-2xl font-bold text-[#e6f1ff]">{formatBoardCurrency(annualRiskBudget)}</p>
              </div>
            </div>
          </div>
          
          <div className="rounded-lg p-4" style={{ background: 'rgba(168, 85, 247, 0.10)', border: '1px solid rgba(168, 85, 247, 0.30)' }}>
            <div className="flex items-center">
              <Target aria-hidden="true" className="h-8 w-8 mr-3" style={{ color: '#c4b5fd' }} />
              <div>
                <p className="text-sm font-medium" style={{ color: '#c4b5fd' }}>Risk Maturity</p>
                <p className="text-2xl font-bold text-[#e6f1ff]">{getReadinessBandLabel(businessData.readinessScore || (100 - riskScore))}</p>
                <p className="text-xs" style={{ color: '#a78bfa' }}>Overall readiness</p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg p-4">
          <h3 className="text-lg font-semibold text-[#e6f1ff] mb-3">Board Readiness Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <strong>Risk Profile:</strong> {businessData.industryBenchmark?.riskProfile || 'Moderate'}
            </div>
            <div>
              <strong>Regulatory Framework:</strong> {regulatoryInfo.framework}
            </div>
            <div>
              <strong>Recovery Capability:</strong> {recoveryDays} days
            </div>
          </div>
        </div>
      </div>

      {/* FOUNDATIONS_AI_ROADMAP Phase 5 Slice 7 — PRISM Technical Posture
          (Cross-Product Insights) — wires the four deterministic cross-product
          narratives into the Board Report so the PDF export captures them.
          Mirrors the purple-accent visual language from the Compass Business
          Intelligence view; html2canvas rasterizes this DOM into the PDF.
          Whole card silent when no PRISM link (all four narratives absent). */}
      {(businessData.recoveryCrossCheck?.divergent === true
        || businessData.prismComplianceNarrative?.present === true
        || businessData.prismMaturityNarrative?.present === true
        || businessData.prismEvidenceNarrative?.present === true
        || businessData.prismCmmcL2TimelineNarrative?.present === true) && (
        <div
          className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6"
          role="region"
          aria-label="PRISM technical posture cross-product insights"
        >
          <h2 className="text-xl font-bold text-[#e6f1ff] mb-2">PRISM Technical Posture</h2>
          <p className="text-xs text-[#8892b0] mb-5">
            Cross-product signals from the PRISM technical assessment, surfaced for the board.
          </p>
          <div className="space-y-4">
            {businessData.recoveryCrossCheck?.divergent === true && (
              <div className="pl-3 border-l-2 border-[#a855f7]">
                <div className="flex items-start gap-2">
                  <AlertTriangle aria-hidden="true" className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#a855f7]" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#a855f7] mb-1">
                      Strategic vs technical recovery posture differs
                    </p>
                    <p className="text-sm text-[#ccd6f6] leading-relaxed">
                      Strategic estimate: <span className="font-semibold text-[#e6f1ff]">{businessData.recoveryCrossCheck.recoveryDays} days</span>
                      {' — '}
                      PRISM technical posture implies <span className="font-semibold text-[#e6f1ff]">{businessData.recoveryCrossCheck.impliedDays} days</span>
                      {' '}(CSF 2.0 Recovery Coverage compounded across recover-relevant ATT&amp;CK techniques).
                    </p>
                    <p className="text-sm text-[#ccd6f6] leading-relaxed mt-1.5">
                      {businessData.recoveryCrossCheck.direction === 'overcautious'
                        ? 'Your self-reported recovery estimate is more cautious than your deployed backup/DR capabilities warrant. Likely cause: incident response plan under-tested. Recommend a tabletop exercise to reconcile assumptions with actual capability.'
                        : 'Your self-reported recovery estimate is more optimistic than your deployed backup/DR capabilities support. Likely cause: backups untested or DR runbook incomplete. Recommend verifying restore procedures end-to-end before the next assessment cycle.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {businessData.prismComplianceNarrative?.present === true && (
              <div className="pl-3 border-l-2 border-[#a855f7]">
                <div className="flex items-start gap-2">
                  <Shield aria-hidden="true" className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#a855f7]" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#a855f7] mb-1">
                      PRISM compliance posture
                    </p>
                    <p className="text-sm text-[#ccd6f6] leading-relaxed">
                      {businessData.prismComplianceNarrative.sentence}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {businessData.prismMaturityNarrative?.present === true && (
              <div className="pl-3 border-l-2 border-[#a855f7]">
                <div className="flex items-start gap-2">
                  <Shield aria-hidden="true" className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#a855f7]" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#a855f7] mb-1">
                      PRISM P/D/R maturity
                    </p>
                    <p className="text-sm text-[#ccd6f6] leading-relaxed">
                      {businessData.prismMaturityNarrative.sentence}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {businessData.prismEvidenceNarrative?.present === true && (
              <div className="pl-3 border-l-2 border-[#a855f7]">
                <div className="flex items-start gap-2">
                  <Shield aria-hidden="true" className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#a855f7]" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#a855f7] mb-1">
                      PRISM engagement density
                    </p>
                    <p className="text-sm text-[#ccd6f6] leading-relaxed">
                      {businessData.prismEvidenceNarrative.sentence}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {businessData.prismCmmcL2TimelineNarrative?.present === true && (
              <div className="pl-3 border-l-2 border-[#a855f7]">
                <div className="flex items-start gap-2">
                  <Shield aria-hidden="true" className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#a855f7]" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#a855f7] mb-1">
                      PRISM {cmmcHeadingLabel(businessData.prismCmmcL2TimelineNarrative)} readiness
                    </p>
                    <p className="text-sm text-[#ccd6f6] leading-relaxed">
                      {businessData.prismCmmcL2TimelineNarrative.sentence}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Financial Impact Modeling */}
      <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
        <h2 className="text-xl font-bold text-[#e6f1ff] mb-6">Financial Impact Modeling</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          {/* Insurance Analysis */}
          <div className="border border-[rgba(255,255,255,0.12)] rounded-lg p-6">
            <div className="flex items-center mb-4">
              <Shield aria-hidden="true" className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="text-lg font-semibold text-[#e6f1ff]">Insurance Coverage Analysis</h3>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-[#a8b2d1]">Total Exposure:</span>
                <span className="font-semibold">{formatBoardCurrency(revenueAtRisk)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#a8b2d1]">Total Coverage:</span>
                <span className={`font-semibold ${insuranceCoverage.hasPolicy ? 'text-blue-600' : 'text-[#5a6a8a]'}`}>
                  {insuranceCoverage.hasPolicy ? formatBoardCurrency((businessData.cyberInsurance?.coverageLimit || 0)) : 'No Policy'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#a8b2d1]">{insuranceCoverage.hasPolicy ? 'Coverage per Incident:' : 'Insurance Coverage:'}</span>
                <span className={`font-semibold ${insuranceCoverage.hasPolicy ? 'text-green-600' : 'text-[#5a6a8a]'}`}>
                  {insuranceCoverage.hasPolicy ? formatBoardCurrency(insuranceCoverage.effectiveCoverage) : 'No Policy'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#a8b2d1]">Coverage Percentage:</span>
                <span className={`font-semibold ${
                  insuranceCoverage.coveragePercentage === null ? 'text-[#5a6a8a]' :
                  insuranceCoverage.coveragePercentage > 0.6 ? 'text-green-600' :
                  insuranceCoverage.coveragePercentage > 0.3 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {insuranceCoverage.coveragePercentage === null ? 'Not measured' : `${(insuranceCoverage.coveragePercentage * 100).toFixed(1)}%`}
                </span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-[#e6f1ff] font-medium">Coverage Gap:</span>
                <span className={`font-bold ${insuranceGap === null ? 'text-[#5a6a8a]' : insuranceGap > 0 ? 'text-[#f87171]' : 'text-[#22c55e]'}`}>{formatBoardCurrency(insuranceGap)}</span>
              </div>
              <div className={`p-3 rounded-lg mt-4 ${insuranceCoverage.hasPolicy ? 'bg-[rgba(0,217,255,0.06)]' : 'bg-[rgba(239,68,68,0.1)]'}`}>
                <p className="text-sm text-[#ccd6f6]">
                  <strong>
                    {insuranceCoverage.hasPolicy ? 'Assessment:' : 'Critical Gap:'}
                  </strong>
                  {insuranceCoverage.coveragePercentage === null
                    ? ' Insurance coverage adequacy could not be assessed because revenue-at-risk has not been measured for this assessment.'
                    : (insuranceCoverage.hasPolicy
                        ? `Your cyber insurance covers ${(insuranceCoverage.coveragePercentage * 100).toFixed(1)}% of potential losses. ${
                            insuranceCoverage.coveragePercentage < 0.6
                              ? `Consider increasing coverage by ${formatBoardCurrency((revenueAtRisk as number) * 0.6 - (insuranceCoverage.effectiveCoverage as number))} to reach 60% industry standard.`
                              : 'Coverage appears adequate for standard incidents.'
                          }`
                        : `No cyber insurance detected. This creates ${formatBoardCurrency(insuranceGap)} in unprotected exposure. Consider immediate cyber insurance procurement.`
                      )
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Market Value Impact — scaled by risk score and regulatory pressure */}
          {(() => {
            const regPressure = businessData.industryBenchmark?.regulatoryPressure || 'Standard';
            const pressureFactor = regPressure === 'High' ? 1.4 : regPressure === 'Moderate' ? 1.15 : 1.0;
            const riskFactor = riskScore / 100; // 0-1 scale
            const baseImpactLow = Math.round(5 + riskFactor * 8 * pressureFactor);
            const baseImpactHigh = Math.round(baseImpactLow * 1.8);
            const recoveryLow = Math.round(baseImpactLow * 0.4);
            const recoveryHigh = Math.round(baseImpactHigh * 0.5);
            const brandYears = recoveryDays > 30 ? '3-5' : recoveryDays > 14 ? '2-3' : '1-2';
            return (
              <div className="border border-[rgba(255,255,255,0.12)] rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <TrendingDown aria-hidden="true" className="h-6 w-6 text-red-600 mr-3" />
                  <h3 className="text-lg font-semibold text-[#e6f1ff]">Market Value Impact</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-[#a8b2d1]">Immediate Impact:</span>
                    <span className="font-semibold text-red-600">-{baseImpactLow}% to -{baseImpactHigh}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#a8b2d1]">6-Month Recovery:</span>
                    <span className="font-semibold text-yellow-600">-{recoveryLow}% to -{recoveryHigh}%</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-[#e6f1ff] font-medium">Long-term Brand Impact:</span>
                    <span className="font-bold text-red-600">{brandYears} years</span>
                  </div>
                  <div className="p-3 rounded-lg mt-4" style={{ background: 'rgba(0, 217, 255, 0.06)', border: '1px solid rgba(0, 217, 255, 0.25)' }}>
                    <p className="text-sm text-[#ccd6f6]">
                      <strong>Estimated for {industry}:</strong> Based on {getRiskBandLabel(riskScore)} risk level and {regPressure.toLowerCase()} regulatory pressure. Recovery timeline of {recoveryDays} days factors into brand impact duration.
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Regulatory & Compliance */}
      <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
        <h2 className="text-xl font-bold text-[#e6f1ff] mb-6">Regulatory & Compliance</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          <div className="rounded-lg p-6" style={{ background: 'rgba(220, 38, 38, 0.08)', border: '1px solid rgba(220, 38, 38, 0.35)' }}>
            <div className="flex items-center mb-4">
              <AlertCircle aria-hidden="true" className="h-6 w-6 mr-3" style={{ color: '#fca5a5' }} />
              <h3 className="text-lg font-semibold text-[#e6f1ff]">Penalty Exposure</h3>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-sm" style={{ color: '#fca5a5' }}>Framework: {regulatoryInfo.framework}</p>
                <p className="text-xl font-bold text-[#e6f1ff]">{formatBoardCurrency(regulatoryInfo.maxPenalty)}</p>
                <p className="text-xs" style={{ color: '#fca5a5' }}>Maximum penalty</p>
              </div>
              <div>
                <p className="text-sm" style={{ color: '#fca5a5' }}>Industry Average:</p>
                <p className="text-lg font-semibold text-[#ccd6f6]">{formatBoardCurrency(regulatoryInfo.avgPenalty)}</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg p-6" style={{ background: 'rgba(234, 179, 8, 0.08)', border: '1px solid rgba(234, 179, 8, 0.35)' }}>
            <div className="flex items-center mb-4">
              <CheckCircle aria-hidden="true" className="h-6 w-6 mr-3" style={{ color: '#fde047' }} />
              <h3 className="text-lg font-semibold text-[#e6f1ff]">Compliance Readiness</h3>
            </div>
            <div className="space-y-3">
              {(() => {
                // Compliance readiness — driven by governance score (policy/process maturity)
                const governanceScore = businessData.advancedAnalytics?.big6UnifiedParams?.big6Scores?.governance;
                const complianceScore = governanceScore != null
                  ? Math.round(governanceScore)
                  : (100 - riskScore);
                const getComplianceStatus = () => {
                  if (complianceScore >= 80) return { level: 'High', color: '#e6f1ff', bg: 'rgba(34, 197, 94, 0.18)', border: 'rgba(34, 197, 94, 0.4)', icon: '✓' };
                  if (complianceScore >= 60) return { level: 'Moderate', color: '#e6f1ff', bg: 'rgba(234, 179, 8, 0.18)', border: 'rgba(234, 179, 8, 0.4)', icon: '⚠' };
                  return { level: 'Needs Improvement', color: '#e6f1ff', bg: 'rgba(220, 38, 38, 0.18)', border: 'rgba(220, 38, 38, 0.4)', icon: '⚡' };
                };

                const requirements = (businessData.industryBenchmark?.complianceFrameworks || []).length > 0
                  ? businessData.industryBenchmark.complianceFrameworks.slice(0, 4)
                  : ['NIST Framework', 'Industry Standards', 'Basic Security Controls'];

                const status = getComplianceStatus();

                return (
                  <>
                    <div className="p-3 rounded-lg mb-3" style={{ background: status.bg, border: `1px solid ${status.border}` }}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium" style={{ color: status.color }}>Current Readiness:</span>
                        <span className="text-lg font-bold" style={{ color: status.color }}>{status.icon} {status.level}</span>
                      </div>
                      <div className="text-xs text-[#a8b2d1] mt-1">
                        Based on {getReadinessBandLabel(complianceScore)} governance maturity{governanceScore != null ? ' (Big 6)' : ''}
                      </div>
                    </div>
                    <div className="text-sm">
                      <p className="font-medium text-[#e6f1ff] mb-2">Key Requirements ({industry}):</p>
                      {requirements.map((req: string, index: number) => (
                        <div key={index} className="flex items-center mb-1">
                          <div className="w-1.5 h-1.5 rounded-full mr-2" style={{ background: '#fde047' }}></div>
                          <span className="text-[#ccd6f6]">{req}</span>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          <div className="rounded-lg p-6" style={{ background: 'rgba(0, 255, 136, 0.06)', border: '1px solid rgba(0, 255, 136, 0.35)' }}>
            <div className="flex items-center mb-4">
              <Target aria-hidden="true" className="h-6 w-6 mr-3" style={{ color: '#00ff88' }} />
              <h3 className="text-lg font-semibold text-[#e6f1ff]">Audit Readiness</h3>
            </div>
            <div className="space-y-3">
              {(() => {
                // Audit readiness — weighted from governance + culture (process + people preparedness)
                const big6 = businessData.advancedAnalytics?.big6UnifiedParams?.big6Scores;
                const auditScore = big6
                  ? Math.round((big6.governance * 0.5 + big6.culture * 0.3 + big6.people * 0.2))
                  : (100 - riskScore);
                return (
                  <>
                    <div>
                      <p className="text-sm" style={{ color: '#86efac' }}>Current Score:</p>
                      <p className="text-2xl font-bold text-[#e6f1ff]">{getReadinessBandLabel(auditScore)}</p>
                    </div>
                    <div className="text-xs" style={{ color: '#86efac' }}>
                      {big6 ? 'Governance + Culture + People weighted assessment' : 'Based on overall assessment'}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Technical Security Posture — only when PRISM is connected */}
      {businessData.prismContext && (() => {
        const pc = businessData.prismContext!;
        const pdr = pc.pdr_maturity;
        const pdrBar = (label: string, value: number | null, pattern: string) => {
          const pct = value != null ? Math.min((value / 4) * 100, 100) : 0;
          const barStyles: Record<string, React.CSSProperties> = {
            solid: { background: '#4f46e5', width: `${pct}%`, height: '100%', borderRadius: 5 },
            striped: {
              background: 'repeating-linear-gradient(135deg, #6366f1, #6366f1 3px, #818cf8 3px, #818cf8 6px)',
              width: `${pct}%`, height: '100%', borderRadius: 5,
            },
            dotted: {
              background: '#818cf8',
              backgroundImage: 'radial-gradient(circle, #4f46e5 1px, transparent 1px)',
              backgroundSize: '5px 5px',
              width: `${pct}%`, height: '100%', borderRadius: 5,
            },
          };
          return (
            <div key={label} className="flex items-center gap-3 mb-2">
              <span className="text-sm font-semibold w-16" style={{ color: '#ccd6f6' }}>{label}</span>
              <div style={{ flex: 1, height: 12, background: 'rgba(255, 255, 255, 0.08)', borderRadius: 6, overflow: 'hidden' }}>
                <div style={barStyles[pattern]} />
              </div>
              <span className="text-sm font-semibold w-10 text-right" style={{ color: 'var(--ra-text-primary)' }}>
                {value != null ? `${value}/4` : '—'}
              </span>
            </div>
          );
        };

        return (
          <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
            <h2 className="text-xl font-bold text-[#e6f1ff] mb-2">Technical Security Posture</h2>
            <p className="text-sm text-[#8892b0] mb-6">PRISM security control assessment{pc.project_count > 1 ? ` across ${pc.project_count} projects` : ''} — not a regulatory compliance determination</p>

            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {/* 2026-10-29: no compliance % outside SPRS. Facts from PRISM #291 instead:
                  open gaps (critical where known) and % of applicable controls evaluated.
                  Old-shape / absent facts read "Not measured", never a % or a 0. */}
              {(() => {
                const facts = complianceFactsDisplay(pc.compliance_facts);
                return (
                  <div className="border border-[rgba(255,255,255,0.12)] rounded-lg p-4 text-center" data-testid="prism-compliance-facts">
                    <div className="text-2xl font-bold" style={{ color: 'var(--ra-cyber-aqua)' }}>
                      {facts.openGaps}
                    </div>
                    <div className="text-xs text-[#8892b0] mt-1">Open Compliance Gaps</div>
                    {facts.measured && (
                      <div className="text-xs mt-1" style={{ color: 'var(--ra-cyber-aqua)' }}>
                        {[facts.criticalDetail, facts.evaluatedDetail].filter(Boolean).join(' · ')}
                      </div>
                    )}
                    {(pc.frameworks_assessed || []).length > 0 && (
                      <div className="text-xs mt-1" style={{ color: 'var(--ra-cyber-aqua)' }}>
                        {pc.frameworks_assessed.map(f => f.split(' (')[0]).join(', ')}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="border border-[rgba(255,255,255,0.12)] rounded-lg p-4 text-center">
                <div className="text-2xl font-bold" style={{ color: 'var(--ra-cyber-aqua)' }}>
                  {(pc.frameworks_assessed || []).length}
                </div>
                <div className="text-xs text-[#8892b0] mt-1">Frameworks Assessed</div>
              </div>
              <div className="border border-[rgba(255,255,255,0.12)] rounded-lg p-4 text-center">
                <div className="text-2xl font-bold" style={{ color: 'var(--ra-cyber-aqua)' }}>
                  {pc.evidence_count}
                </div>
                <div className="text-xs text-[#8892b0] mt-1">Evidence Documents</div>
              </div>
              <div className="border border-[rgba(255,255,255,0.12)] rounded-lg p-4 text-center">
                <div className="text-2xl font-bold" style={{ color: 'var(--ra-cyber-aqua)' }}>
                  {pc.total_assets}
                </div>
                <div className="text-xs text-[#8892b0] mt-1">Assets Inventoried</div>
              </div>
            </div>

            {/* P/D/R Maturity */}
            {pdr && (pdr.protect != null || pdr.detect != null || pdr.respond != null) && (
              <div className="border border-[rgba(255,255,255,0.12)] rounded-lg p-5 mb-6">
                <h3 className="text-base font-semibold text-[#e6f1ff] mb-3">Protect / Detect / Respond Maturity</h3>
                {pdrBar('Protect', pdr.protect, 'solid')}
                {pdrBar('Detect', pdr.detect, 'striped')}
                {pdrBar('Respond', pdr.respond, 'dotted')}
                <p className="text-xs text-[#8892b0] mt-2">DoDCAR scale: 0 = No capability, 4 = Optimized</p>
              </div>
            )}

            {/* Deployed Controls */}
            {(pc.deployed_controls || []).length > 0 && (
              <div className="border border-[rgba(255,255,255,0.12)] rounded-lg p-5 mb-6">
                <h3 className="text-base font-semibold text-[#e6f1ff] mb-3">Key Security Controls</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {pc.deployed_controls
                    .sort((a, b) => (b.effectiveness || 0) - (a.effectiveness || 0))
                    .slice(0, 8)
                    .map((ctrl, i) => (
                      <div key={i} className="border border-[rgba(255,255,255,0.12)] rounded p-3" style={{ background: 'rgba(255, 255, 255, 0.04)' }}>
                        <div className="text-sm font-medium text-[#e6f1ff] truncate">{ctrl.name}</div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-[#8892b0]">{ctrl.type || 'Control'}</span>
                          {ctrl.effectiveness && (
                            <span className="text-xs font-semibold" style={{ color: 'var(--ra-cyber-aqua)' }}>
                              {ctrl.effectiveness}/5
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Top Gaps */}
            {(pc.top_gaps || []).length > 0 && (
              <div className="rounded-lg p-5" style={{ background: 'rgba(220, 38, 38, 0.08)', border: '1px solid rgba(220, 38, 38, 0.35)' }}>
                <h3 className="text-base font-semibold mb-3" style={{ color: '#fca5a5' }}>Priority Gaps Identified</h3>
                <ul className="space-y-2">
                  {pc.top_gaps.slice(0, 5).map((gap, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#fecaca' }}>
                      <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#f87171' }} />
                      <span>
                        <strong>{gap.control}</strong> ({gap.framework}) — {gap.description}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })()}

      {/* Competitive Intelligence */}
      <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
        <h2 className="text-xl font-bold text-[#e6f1ff] mb-6">Competitive Intelligence</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          <div className="border border-[rgba(255,255,255,0.12)] rounded-lg p-6">
            <div className="flex items-center mb-4">
              <TrendingDown aria-hidden="true" className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-[#e6f1ff]">Market Share Vulnerability</h3>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-[#a8b2d1]">Recovery Timeline:</span>
                <span className="font-semibold">{recoveryDays} days</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#a8b2d1]">Potential Share Loss:</span>
                <span className="font-semibold text-red-600">{(competitorAdvantage * 100).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#a8b2d1]">Customer Acquisition Cost:</span>
                <span className="font-semibold text-orange-600">+{Math.round(100 + (recoveryDays / 30) * 80)}% ({Math.max(6, Math.round(recoveryDays / 30 * 8))} months)</span>
              </div>
              <div className="p-3 rounded-lg" style={{ background: 'rgba(220, 38, 38, 0.10)', border: '1px solid rgba(220, 38, 38, 0.30)' }}>
                <p className="text-sm text-[#ccd6f6]">
                  <strong>Risk:</strong> {businessData.competitiveRisks?.primary ||
                    `Competitors gain market position and customer relationships during ${recoveryDays}-day business disruption.`}
                </p>
                {businessData.competitiveRisks?.secondary && (
                  <p className="text-xs mt-1" style={{ color: '#fca5a5' }}>
                    {businessData.competitiveRisks.secondary}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="border border-[rgba(255,255,255,0.12)] rounded-lg p-6">
            <div className="flex items-center mb-4">
              <Shield aria-hidden="true" className="h-6 w-6 text-green-600 mr-3" />
              <h3 className="text-lg font-semibold text-[#e6f1ff]">Security as Differentiator</h3>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-[#a8b2d1]">Security Maturity:</span>
                <span className="font-semibold text-green-600">{getReadinessBandLabel(100 - riskScore)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#a8b2d1]">vs Industry:</span>
                <span className="font-semibold">{getReadinessBandLabel(businessData.industryBenchmark?.averageReadiness || 65)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#a8b2d1]">Competitive Position:</span>
                <span className="font-semibold text-blue-600">
                  {100 - riskScore > (businessData.industryBenchmark?.averageReadiness || 65) ? 'Above Average' : 100 - riskScore === (businessData.industryBenchmark?.averageReadiness || 65) ? 'At Average' : 'Below Average'}
                </span>
              </div>
              <div className="p-3 rounded-lg" style={{ background: 'rgba(0, 255, 136, 0.08)', border: '1px solid rgba(0, 255, 136, 0.30)' }}>
                <p className="text-sm text-[#ccd6f6]">
                  <strong>Opportunity:</strong> {100 - riskScore > (businessData.industryBenchmark?.averageReadiness || 65) ? 'Leverage security posture in RFPs and customer acquisition.' : 'Improve security posture to competitive advantage levels.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Executive Recommendations */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex items-center mb-4">
          <Target aria-hidden="true" className="h-8 w-8 text-white mr-3" />
          <h2 className="text-xl font-bold">Board Action Items</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h3 className="font-semibold mb-2">Immediate (30 days)</h3>
            <ul className="text-sm space-y-1 text-blue-100">
              {businessData.boardActionItems?.immediate && businessData.boardActionItems.immediate.length > 0 ? (
                businessData.boardActionItems.immediate.map((item, index) => (
                  <li key={index}>• {item}</li>
                ))
              ) : (
                <>
                  <li>• Review cyber insurance coverage gaps</li>
                  <li>• Validate incident response procedures</li>
                  <li>• Assess third-party vendor security</li>
                </>
              )}
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Strategic (90 days)</h3>
            <ul className="text-sm space-y-1 text-blue-100">
              {businessData.boardActionItems?.strategic && businessData.boardActionItems.strategic.length > 0 ? (
                businessData.boardActionItems.strategic.map((item, index) => (
                  <li key={index}>• {item}</li>
                ))
              ) : (
                <>
                  <li>• Implement security awareness program</li>
                  <li>• Enhance regulatory compliance framework</li>
                  <li>• Develop competitive security messaging</li>
                </>
              )}
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Governance (Ongoing)</h3>
            <ul className="text-sm space-y-1 text-blue-100">
              {businessData.boardActionItems?.governance && businessData.boardActionItems.governance.length > 0 ? (
                businessData.boardActionItems.governance.map((item, index) => (
                  <li key={index}>• {item}</li>
                ))
              ) : (
                <>
                  <li>• Quarterly board security reporting</li>
                  <li>• Annual cyber insurance evaluation</li>
                  <li>• Continuous threat landscape monitoring</li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

// ============ MAIN APP COMPONENT ============
const App: React.FC = () => {
  const location = useLocation(); // Add useLocation hook
  const [currentPage, setCurrentPage] = useState<string>('business-intelligence');
  const [businessData, setBusinessData] = useState<BusinessIntelligenceData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [outsidePressureData, setOutsidePressureData] = useState<any>(null);
  const [user, setUser] = useState<UserType>({
    id: '1',
    name: 'Business User',
    email: 'user@company.com',
    firstName: 'Business',
    lastName: 'User'
  });

  // Update currentPage based on URL path - now triggers on location changes
  React.useEffect(() => {
    const updateCurrentPage = () => {
      const pathname = window.location.pathname;
      console.log('🔍 Current pathname:', pathname);
      
      if (pathname.includes('/outside-pressures')) {
        console.log('📍 Setting currentPage to: outside-pressures');
        setCurrentPage('outside-pressures');
      } else if (pathname.includes('/investment')) {
        console.log('📍 Setting currentPage to: investment-optimization');
        setCurrentPage('investment-optimization');
      } else if (pathname.includes('/scenarios')) {
        console.log('📍 Setting currentPage to: strategic-scenarios');
        setCurrentPage('strategic-scenarios');
      } else if (pathname.includes('/reports')) {
        console.log('📍 Setting currentPage to: board-reports');
        setCurrentPage('board-reports');
      } else {
        console.log('📍 Setting currentPage to: business-intelligence (default)');
        setCurrentPage('business-intelligence');
      }
    };

    // Update on initial load
    updateCurrentPage();

    // Listen for URL changes (for navigation without page reload)
    window.addEventListener('popstate', updateCurrentPage);
    
    // Cleanup listener
    return () => {
      window.removeEventListener('popstate', updateCurrentPage);
    };
  }, [location]); // Add location as dependency to trigger on React Router navigation

  // Session to show: URL, stored, then the caller's own latest (services/currentSession.ts).
  // null means the viewer has no assessment — never substitute an id.
  const getAssessmentId = (): Promise<string | null> => resolveCurrentSessionId();

  // Load outside pressure data
  const loadOutsidePressureData = async (): Promise<void> => {
    try {
      const assessmentId = await getAssessmentId();
      if (!assessmentId) return;
      console.log('🔍 Loading outside pressure data for assessment ID:', assessmentId);
      
      const response = await fetch(apiConfig.riskIntelligence.getOutsidePressures(assessmentId), { headers: authHeaders() });
      if (response.ok) {
        const result = await response.json();
        console.log('Outside Pressure Data received:', result);
        if (result.success && result.outsidePressures) {
          // Include personalizationContext with the outsidePressures data
          const dataWithContext = {
            ...result.outsidePressures,
            personalizationContext: result.personalizationContext
          };
          setOutsidePressureData(dataWithContext);
          console.log('Outside Pressure Data set with context:', dataWithContext);
        }
      }
    } catch (error) {
      console.error('Failed to fetch outside pressure data:', error);
    }
  };

  // Load business intelligence data from database API
  const loadBusinessIntelligence = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      
      const assessmentId = await getAssessmentId();
      if (!assessmentId) {
        setError('No assessment found for your account yet.');
        return;
      }
      console.log('🔍 BoardReports - Using assessmentId for business intelligence API:', assessmentId);

      // Call your working database API
      const apiBaseUrl = apiConfig.getApiBaseUrl();
      const apiUrl = `${apiBaseUrl}/business-intelligence/${assessmentId}`;

      // Was sending `Authorization: Bearer ` (empty) when no token was stored — a malformed
      // credential rather than an absent one. See services/authHeaders.ts.
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Database API failed: ${response.status} ${response.statusText}`);
      }

      const apiResponse = await response.json();
      
      // DEBUG: Log the API response to understand what we're getting

      // Check if response has the expected structure
      if (!apiResponse.success) {
        throw new Error('API returned error: ' + (apiResponse.message || 'Unknown error'));
      }


      // Transform API response to match expected format (FIXED - matches actual backend API)
      const data = {
        assessmentId: parseInt(apiResponse.assessmentId),
        companyName: apiResponse.companyName || 'Unknown Company',
        industry: apiResponse.industry || 'Unknown', 
        // Preserve null/undefined honestly — a missing measurement, not a plausible $0.
        annualRevenue: apiResponse.annualRevenue === undefined ? null : apiResponse.annualRevenue,
        // Same treatment: an unmeasured revenue-at-risk string is not a plausible '$0'.
        revenueAtRisk: apiResponse.revenueAtRisk ?? null,
        // Same treatment as annualRevenue directly above: an unmeasured revenue-at-risk is
        // not a plausible $0. BoardReports gates every consumer of this field on
        // revenueAtRiskMeasured rather than trusting raw arithmetic against it.
        revenueAtRiskRaw: (apiResponse.revenueAtRiskRaw === null || apiResponse.revenueAtRiskRaw === undefined)
          ? null
          : apiResponse.revenueAtRiskRaw,
        // Measured values only: a real 0 used to become 59 / 20 here (boardReportRiskFields.ts).
        ...boardReportRiskFields(apiResponse),
        isActualRevenue: Boolean(apiResponse.annualRevenue && apiResponse.annualRevenue > 0),
        calculationSource: 'business_intelligence_api',
        recoveryLevel: 'Calculated',
        industryBenchmark: apiResponse.industryBenchmarks ? {
          industryName: apiResponse.industryBenchmarks.industryName,
          riskMultiplier: apiResponse.industryBenchmarks.riskMultiplier,
          recoveryComplexity: apiResponse.industryBenchmarks.recoveryComplexity,
          threatLandscape: apiResponse.industryBenchmarks.threatLandscape,
          focusAreas: apiResponse.industryBenchmarks.focusAreas,
          complianceFrameworks: apiResponse.industryBenchmarks.complianceFrameworks,
          riskProfile: apiResponse.industryBenchmarks.industrySpecificFactors?.riskProfile,
          regulatoryPressure: apiResponse.industryBenchmarks.industrySpecificFactors?.regulatoryPressure,
          averageReadiness: apiResponse.industryBenchmarks?.averageReadiness || 41,
          averageRiskScore: boardReportRiskFields(apiResponse).riskScore,
          averageRecoveryDays: boardReportRiskFields(apiResponse).recoveryDays,
          // Suppress the whole percentage — never compute it from a substituted numerator or denominator.
          revenueAtRiskPercent: (apiResponse.revenueAtRiskRaw === null || apiResponse.revenueAtRiskRaw === undefined ||
            apiResponse.annualRevenue === null || apiResponse.annualRevenue === undefined)
            ? null
            : (apiResponse.revenueAtRiskRaw / apiResponse.annualRevenue * 100).toFixed(1),
          recommendedSecurityPercent: 3.5,
          investmentRange: { min: 2.8, optimal: 3.5, max: 4.5 }
        } : {
          averageReadiness: 41,
          averageRiskScore: 59,
          averageRecoveryDays: 20,
          revenueAtRiskPercent: 10.3,
          recommendedSecurityPercent: 3.5,
          investmentRange: { min: 2.8, optimal: 3.5, max: 4.5 }
        },
        investmentScenarios: apiResponse.investmentScenarios || [],
        investmentRange: {
          min: 2.8,
          optimal: 3.5, 
          max: 4.5
        },
        // Add cyber insurance data from API response
        cyberInsurance: apiResponse.cyberInsurance || {
          coverageLimit: 0,
          perIncidentLimit: 0,
          ransomwareCoverage: 0
        },
        // Add board action items from API response
        boardActionItems: apiResponse.boardActionItems || {
          immediate: [],
          strategic: [],
          governance: []
        },
        // Add advanced analytics with Big 6 unified parameters
        advancedAnalytics: apiResponse.advancedAnalytics || undefined,
        // Broker placement block — present only when BROKER_PLACEMENT_ENABLED (backend flag).
        placement: apiResponse.placement || undefined,
        // PRISM Technical Security Context (null if no PRISM link)
        prismContext: apiResponse.prismContext || null,
        // CSF 2.0 Phase 1 Slice 3 — Recovery Cross-Check (Foundations × PRISM).
        // Phase 5 Slice 1 — PRISM compliance narrative on Board Report.
        // Phase 5 Slice 5 — PRISM P/D/R maturity narrative on Board Report.
        // Phase 5 Slice 6 — PRISM evidence-density narrative on Board Report.
        // Phase 5 Slice 4 — PRISM CMMC L2 readiness milestone on Board Report.
        // All shipped backend-side; transform must whitelist each one or the
        // conditional renders never fire client-side (lesson from Slice 1).
        recoveryCrossCheck: apiResponse.recoveryCrossCheck || null,
        prismComplianceNarrative: apiResponse.prismComplianceNarrative || null,
        prismMaturityNarrative: apiResponse.prismMaturityNarrative || null,
        prismEvidenceNarrative: apiResponse.prismEvidenceNarrative || null,
        prismCmmcL2TimelineNarrative: apiResponse.prismCmmcL2TimelineNarrative || null,
      };
      
      // DEBUG: Log the transformed data
      console.log('📊 Business Intelligence Data with Cyber Insurance:', {
        cyberInsurance: data.cyberInsurance,
        revenueAtRisk: data.revenueAtRiskRaw,
        big6UnifiedParams: data.advancedAnalytics?.big6UnifiedParams ? 'Present' : 'Not present'
      });
      
      // Verify we got the correct revenue
      if (data.annualRevenue === 3000000000) {
        // Successfully received revenue from database
      }

      setBusinessData(data);

    } catch (err) {
      // Error loading business intelligence - handled silently
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      
      // No invented figures: the screens render the error (BusinessDataUnavailable).
      setBusinessData(null);
    } finally {
      setLoading(false);
    }
  };

  // Load business intelligence on component mount
  useEffect(() => {
    // Handle page parameter from URL
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    
    if (pageParam) {
      // Map URL parameter to valid page IDs
      const validPages = ['business-intelligence', 'investment-optimization', 'strategic-scenarios', 'board-reports'];
      if (validPages.includes(pageParam)) {
        setCurrentPage(pageParam);
      }
    }
    
    loadBusinessIntelligence();
    loadOutsidePressureData();
  }, []);
  
  // Listen for URL changes
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const pageParam = urlParams.get('page');
      
      if (pageParam) {
        const validPages = ['business-intelligence', 'investment-optimization', 'strategic-scenarios', 'board-reports'];
        if (validPages.includes(pageParam)) {
          setCurrentPage(pageParam);
        }
      } else {
        setCurrentPage('business-intelligence');
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Navigation handlers
  const handleLogout = (): void => {
    // User logging out
    window.location.href = '/';
  };

  const handleRefresh = (): void => {
    // Refreshing business intelligence data
    loadBusinessIntelligence();
    loadOutsidePressureData();
  };

  const handleDefensePlanning = async (): Promise<void> => {
    // Launching Friction Analysis - environment aware
    const assessmentId = await getAssessmentId();
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const defenseBaseUrl = isDevelopment
      ? 'http://localhost:3001'
      : 'https://foundations-risk-aperture.com';
    const defenseUrl = assessmentId
      ? `${defenseBaseUrl}/?assessmentId=${assessmentId}&source=compass`
      : `${defenseBaseUrl}/?source=compass`;
    window.open(defenseUrl, '_blank');
  };

  const handleAssessmentDashboard = (): void => {
    // Navigating to Risk Assessment Dashboard - environment aware
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const dashboardUrl = isDevelopment 
      ? 'http://localhost:5174'
      : 'https://insights-risk-aperture.com';
    window.location.href = dashboardUrl;
  };

  // EXECUTIVE PERSONA NAVIGATION - Clean and focused
  const navigation: NavigationItem[] = [
    { name: 'Business Intelligence', id: 'business-intelligence', icon: BarChart3 },
    { name: 'Investment Optimization', id: 'investment-optimization', icon: TrendingUp },
    { name: 'Strategic Scenarios', id: 'strategic-scenarios', icon: Target },
    { name: 'Board Reports', id: 'board-reports', icon: FileText },
  ];

  const renderCurrentPage = () => {
    const commonProps = {
      businessData,
      loading
    };

      switch (currentPage) {
      case 'business-intelligence':
        return (
          <div className="space-y-6">
            <BusinessIntelligenceDashboard {...commonProps} />
          </div>
        );
      case 'investment-optimization':
        return <InvestmentOptimization businessData={businessData} loading={loading} outsidePressureData={outsidePressureData} loadError={error} />;
      case 'strategic-scenarios':
        return <StrategicScenarios businessData={businessData} loading={loading} loadError={error} />;
      case 'board-reports':
        return <BoardReports businessData={businessData} loading={loading} loadError={error} />;
      case 'outside-pressures':
        // URL or stored session; with neither, the component asks /assessments/latest itself.
        return <OutsidePressuresCenter assessmentId={getStoredSessionId() || undefined} />;
      // case 'pirs':
      default:
        return (
          <div className="space-y-6">
            <BusinessIntelligenceDashboard {...commonProps} />
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#172a45]">
      {/* Main Content Area - Navigation handled by AppLayout wrapper */}
      <div className="flex-1">
        <div className="flex-1 p-8">
          {renderCurrentPage()}
        </div>
      </div>
    </div>
  );
};

export default App;
// Exported for its render test (hook order across the loading -> loaded transition).
export { InvestmentOptimization };
// Exported for its render test (unmeasured-revenue guard across the whole scenario screen).
export { StrategicScenarios };
// Exported for its render test (Task 11 follow-up: unmeasured revenueAtRiskRaw must not crash).
export { BoardReports };